import { defineStore } from 'pinia'
import { ref, computed, toRaw } from 'vue'
import { APIServer } from '@/app/data/api'
import _, { cloneDeep } from 'lodash'
import {
  type ShapeTemplateType,
  DefectPhotoType,
  ChangedItem,
  QuickInputOtherItem,
  UserDefectInputPreset,
  SlidingPopupMode,
} from '@/app/data/type'
import { CloudFile } from '@/lib/CloudFile'
import { CanvasEngineExportEntityType, SHAPES } from '@/lib/CanvasEngine'
import { useCanvasStore } from './canvas'
import {
  getFirstSortKey,
  getLastSortKey,
  isOutSidePhoto,
  isPreviousPhoto,
  isPreviousShape,
  pickShapeScaleFields,
  sortByKeys,
  uploadPathGenerator,
} from '../util'
// 변경 감지 관련 유틸리티 함수 임포트
import { findChangedItems, logChanges } from './deviceEventLog'
import { useAllTaskStore } from './task'
import { GCS_ROOT_PATH } from '@/index'
import { useUndoRedo } from '../undoRedo'
import {
  getCurrentColor,
  getCurrentColorName,
  LENGTH_VALUES,
  WIDTH_VALUES_RANGE,
  PREV_DEFECT_STYLE,
} from '../constant'
import { Defect, FacilityPhoto, Floorplan, DefectPhoto, InspectionType } from '../data/types'
import type { PhotoDrawing } from '../photoDrawing/types'
import { isSamePhotoDrawing } from '../photoDrawing/validate'
import { useFloorplanStore } from '../data/Editor/floorplan'
import Merge from '../../lib/Task/merge'
import {
  saveCurrentStylesToShapeData,
  consumeSavedCurrentStyles,
  calcBecomeCurrentColors,
  calcBecomePreviousColors,
} from '@/app/function/shapeColorToggle'
import { isEffectivelyPrevious, isUnplacedPrevDefect } from '@/app/function/defectMode'
import UTIL from '../../lib/CanvasEngine/helper/canvasUtil'

type shapeId = string

export enum WebEditorMode {
  Default = 'default',
  Export = 'export',
}

export const useWebEditorActionStore = defineStore('web-editor-selection', () => {
  const mode = ref<WebEditorMode>(WebEditorMode.Default)
  const checkedShapeIds = ref<shapeId[]>([])
  const checkedFloorIds = ref<string[]>([])
  const shapeInspectPageMode = ref<SlidingPopupMode>(SlidingPopupMode.None)
  const printOutPageMode = ref<SlidingPopupMode>(SlidingPopupMode.None)
  const facilityPhotoPopupMode = ref<SlidingPopupMode>(SlidingPopupMode.None)

  const docEditPageMode = ref<SlidingPopupMode>(SlidingPopupMode.None)

  const shareLinkPopupMode = ref<SlidingPopupMode>(SlidingPopupMode.None)

  const allClearPageModes = (): void => {
    shapeInspectPageMode.value = SlidingPopupMode.None
    printOutPageMode.value = SlidingPopupMode.None
    facilityPhotoPopupMode.value = SlidingPopupMode.None
    docEditPageMode.value = SlidingPopupMode.None
    shareLinkPopupMode.value = SlidingPopupMode.None
  }

  function toggleShape(shapeId: string): void {
    if (checkedShapeIds.value.includes(shapeId)) {
      checkedShapeIds.value = checkedShapeIds.value.filter(id => id !== shapeId)
    } else {
      checkedShapeIds.value.push(shapeId)
    }
  }

  function toggleFloor(floorId: string): void {
    if (checkedFloorIds.value.includes(floorId)) {
      checkedFloorIds.value = checkedFloorIds.value.filter(id => id !== floorId)
    } else {
      checkedFloorIds.value.push(floorId)
    }
  }

  function clearSelection(): void {
    checkedShapeIds.value = []
    checkedFloorIds.value = []
  }

  return {
    toggleShape,
    toggleFloor,
    checkedShapeIds,
    mode,
    clearSelection,
    checkedFloorIds,
    shapeInspectPageMode,
    printOutPageMode,
    docEditPageMode,
    shareLinkPopupMode,
    allClearPageModes,
    facilityPhotoPopupMode,
  }
})

export const useWebEditorDataStore = defineStore('web-editor-data', () => {
  const selectedShapeId = ref<shapeId>('')

  //결함 사진
  const defectPhotos = ref<DefectPhoto[]>([])

  //결함
  const shapes = ref<Defect[]>([])

  // floor별 shape 인덱스 (shapes.value 변경 시 갱신)
  const _shapesByFloor = computed(() => {
    const map = new Map<string, Defect[]>()
    for (const shape of shapes.value) {
      if (shape.deleted_at) continue
      const floorId = shape.floorplan_id.toString()
      let list = map.get(floorId)
      if (!list) {
        list = []
        map.set(floorId, list)
      }
      list.push(shape)
    }
    return map
  })

  const facilityBranchImages = ref<FacilityPhoto[]>([])

  const PHOTO_TYPE = {
    NEW: 'new',
    PREV: 'prev',
  } as const

  function selectShape(shapeId: shapeId): void {
    const canvasStore = useCanvasStore()
    const actionStore = useWebEditorActionStore()
    // 자동정렬중에는 토스트메시지와 함께 종료
    if (shapeId !== '' && canvasStore.checkAutoAlignModeWithToast()) return
    selectedShapeId.value = shapeId

    if (actionStore.mode == WebEditorMode.Export && shapeId !== '')
      actionStore.mode = WebEditorMode.Default
  }

  // id → shape 매핑 (getShape O(1) 조회용, 배열 순서상 앞선 shape가 키를 선점해 첫 매칭 우선)
  const _shapeById = computed((): Map<string, Defect> => {
    const map = new Map<string, Defect>()
    for (const shape of shapes.value) {
      const key = `${shape.id}`
      if (!map.has(key)) map.set(key, shape)
    }
    return map
  })

  function getShape(shapeId: shapeId): Defect | undefined {
    const found = _shapeById.value.get(`${shapeId}`)
    return found ? { ...found } : undefined
  }

  function getShapeFloor(shapeId: shapeId): {
    floorId: number
    floorName: string
  } {
    const allTaskStore = useAllTaskStore()
    const floorplanStore = useFloorplanStore()
    if (allTaskStore.isNotLoaded()) {
      throw new Error('EditorTaskStore is not loaded')
    }
    const shape = shapes.value.find(shape => `${shape.id}` == `${shapeId}`)
    if (!shape)
      return {
        floorId: -1,
        floorName: '',
      }
    return {
      floorId: Number(shape.floorplan_id),
      floorName: floorplanStore.getFloorplanName(shape.floorplan_id.toString()),
    }
  }

  function isCheckPreviousShape(shapeId: string): boolean {
    const findShape = shapes.value.find(item => `${item.id}` == `${shapeId}`)
    if (!findShape) return false
    if (findShape.shape_type == SHAPES.BRUSH) return false
    const isPreviousHasDefectPhoto = getIsPreviousHasDefectPhoto(shapeId)
    return (
      isPreviousShape(findShape.previous_defect_id?.toString() ?? '') && !isPreviousHasDefectPhoto
    )
  }
  /**
   * 기존 shape를 복사하여 새로운 shape를 생성합니다
   * @param shapeId - 복사할 원본 shape의 ID
   * @param copiedShape - 복사된 shape 데이터
   * @returns 새로 생성된 shape의 ID
   */
  async function copyShape(
    shapeId: string,
    copiedShape: CanvasEngineExportEntityType,
  ): Promise<Defect | undefined> {
    const findShape = cloneDeep(getShape(shapeId))
    if (!findShape) return

    const newShape = { ...findShape, id: -1 }
    newShape.shape_data = { ...findShape.shape_data, ...copiedShape }
    const lastSortKey = getLastSortKey(
      shapes.value.filter(
        defect =>
          `${defect.floorplan_id}` == `${findShape.floorplan_id}` &&
          !defect.deleted_at &&
          defect.shape_type !== SHAPES.BRUSH &&
          (!isPreviousShape(defect.previous_defect_id?.toString() ?? '') ||
            (isPreviousShape(defect.previous_defect_id?.toString() ?? '') &&
              getIsPreviousHasDefectPhoto(defect.id.toString()))),
      ),
    )
    newShape.sort_key = lastSortKey

    const [newShapeResult] = await APIServer.addShape([Merge.returnHasValue(newShape) as Defect])
    const newShapeItem = {
      ...newShapeResult,
      id: newShapeResult.id,
      defect_photos: [] as DefectPhoto[],
    }
    newShapeItem.shape_data.shapeId = newShapeResult.id.toString()

    shapes.value.push(newShapeItem)

    const changes: ChangedItem[] = findChangedItems(
      [],
      [{ ...newShapeItem, localStringId: newShapeItem.id.toString() }],
    )
    logChanges('SHAPES', changes)
    return newShapeItem
  }
  function setShapes(_shapes: Defect[]): void {
    shapes.value = _shapes
  }

  function setDefectPhotos(_defectPhotos: DefectPhoto[]): void {
    defectPhotos.value = _defectPhotos.filter(defectPhoto => !defectPhoto.deleted_at)
  }

  // defect_id:inspection_type → 현회차 사진 존재 여부 캐시 (defectPhotos 변경 시 갱신)
  // inspection_type이 일치하는 non-previous 사진만 현재차로 간주
  const _prevPhotoCache = computed(() => {
    const map = new Map<string, boolean>()
    for (const photo of defectPhotos.value) {
      if (photo.deleted_at) continue
      if (isPreviousPhoto(photo)) continue
      const typeKey = `${photo.defect_id}:${photo.inspection_type ?? 'STATUS_OUTSIDE'}`
      if (!map.has(typeKey)) {
        map.set(typeKey, true)
      }
    }
    return map
  })

  // defect_id → DefectPhoto[] 캐시
  const _photosByDefectId = computed(() => {
    const map = new Map<string, DefectPhoto[]>()
    for (const photo of defectPhotos.value) {
      if (photo.deleted_at) continue
      const key = `${photo.defect_id}`
      let list = map.get(key)
      if (!list) {
        list = []
        map.set(key, list)
      }
      list.push(photo)
    }
    for (const [, photos] of map) {
      sortByKeys(photos)
    }
    return map
  })
  function getIsPreviousHasDefectPhoto(shapeId: shapeId): boolean {
    // _shapeById 맵으로 O(1) 조회 (기존 find 선형 탐색과 동일하게 첫 매칭 우선)
    const shape = _shapeById.value.get(`${shapeId}`)
    const inspectionType = shape?.inspection_type ?? 'STATUS_OUTSIDE'
    if (_prevPhotoCache.value.has(`${shapeId}:${inspectionType}`)) return true
    return !!shape?.is_current
  }

  function getDefectPhotosByTaskId(): DefectPhoto[] {
    return defectPhotos.value.filter(defectPhoto => !defectPhoto.deleted_at)
  }
  // getIsPreviousHasDefectPhoto와 동일 — 외부 호출 호환성을 위해 유지
  function getIsPreviousHasDefectCurrentPhoto(shapeId: shapeId): boolean {
    return getIsPreviousHasDefectPhoto(shapeId)
  }

  // 보라색 표시 대상(순수 전차)인지 판정. is_current=1이면 false.
  function isPrevWithoutCurrentPhoto(shape: Defect): boolean {
    const hasCurrentPhoto = getIsPreviousHasDefectPhoto(`${shape.id}`)
    return isEffectivelyPrevious(shape, hasCurrentPhoto)
  }

  // 전차↔현차 전환 시 상태 캡처/복원 헬퍼
  function captureShapeColorState(shapeId: string): {
    is_current: number
    color: string
    balloonFontColor: string
    balloonFillColor: string
  } | null {
    const shape = shapes.value.find(item => `${item.id}` == shapeId)
    if (!shape) return null
    return {
      is_current: shape.is_current ?? 0,
      color: shape.color,
      balloonFontColor: shape.shape_data?.balloonFontColor ?? '',
      balloonFillColor: shape.shape_data?.balloonFillColor ?? '',
    }
  }

  async function restoreShapeColorState(
    shapeId: string,
    state: {
      is_current: number
      color: string
      balloonFontColor: string
      balloonFillColor: string
    },
  ): Promise<void> {
    const canvasStore = useCanvasStore()
    const findShape = shapes.value.find(item => `${item.id}` == shapeId)
    if (!findShape) return
    const changedShape = {
      ...cloneDeep(findShape),
      is_current: state.is_current,
      color: state.color,
    }
    changedShape.shape_data = {
      ...changedShape.shape_data,
      balloonFontColor: state.balloonFontColor,
      balloonFillColor: state.balloonFillColor,
    }
    changedShape.fields_updated_at = Merge.findChangedField(findShape, changedShape)
    shapes.value = shapes.value.map(s => (`${s.id}` == shapeId ? changedShape : s))
    const { afterDefect } = findNeedUpdateDefect(changedShape, findShape)
    if (afterDefect) {
      sendShapesWithRebase([afterDefect], [findShape]).catch(console.error)
    }
    onColorChanged(shapeId, state.color, state.balloonFontColor, state.balloonFillColor, true)
    const entity = canvasStore.getEntityById(shapeId)
    if (entity) {
      if (state.color === PREV_DEFECT_STYLE.color) entity.disableIndex()
      else entity.enableIndex()
    }
    canvasStore.updateIndex()
  }

  // 전차결함 ↔ 현재차가된전차결함 수동 전환 (사진 없이 현재차로 취급)
  // is_current + 색상 4필드를 단일 트랜잭션으로 변경 → fields_updated_at가 한 번에 일관 기록되고
  // APIServer.updateShapes도 1회만 호출되어 race condition 차단.
  async function setIsBecomeCurrent(shapeId: string, value: number): Promise<void> {
    const undoRedo = useUndoRedo()
    const findShape = shapes.value.find(item => `${item.id}` == shapeId)
    if (!findShape || !isPreviousShape(findShape.previous_defect_id?.toString() ?? '')) return

    // 전환 전 상태 캡처 (undo용)
    const beforeState = captureShapeColorState(shapeId)
    if (!beforeState) return

    const previousShape = cloneDeep(findShape)
    const changedShape = cloneDeep(findShape)
    changedShape.is_current = value

    // 색상 계산 전에 shape_data를 보장 (계산 내부에서 shape_data를 직접 참조하므로)
    if (!changedShape.shape_data) {
      changedShape.shape_data = {} as Defect['shape_data']
    }

    // 색상 계산 (savedCurrentStyles 보존/복원 포함)
    const colors =
      value === 1 ? calcBecomeCurrentColors(changedShape) : calcBecomePreviousColors(changedShape)

    // 색상 4필드를 한 번에 적용
    changedShape.color = colors.borderColor
    changedShape.shape_data.color = colors.borderColor
    changedShape.shape_data.balloonFontColor = colors.fontColor
    changedShape.shape_data.balloonFillColor = colors.fillColor
    changedShape.shape_data.isChanged = false

    // fields_updated_at 1회 계산
    changedShape.fields_updated_at = Merge.findChangedField(previousShape, changedShape)

    // store 업데이트 + API 1회 호출
    shapes.value = shapes.value.map(s => (`${s.id}` == shapeId ? changedShape : s))
    const changes: ChangedItem[] = findChangedItems(
      [{ ...previousShape, localStringId: previousShape.id.toString() }],
      [{ ...changedShape, localStringId: changedShape.id.toString() }],
    )
    logChanges('SHAPES', changes)
    const { afterDefect } = findNeedUpdateDefect(changedShape, previousShape)
    if (afterDefect) {
      sendShapesWithRebase([afterDefect], [previousShape]).catch(console.error)
    }

    // 캔버스 entity 즉시 갱신 (Step E: stale 캔버스 차단)
    const _canvasStore = useCanvasStore()
    const entity = _canvasStore.getEntityById(shapeId)
    if (entity) {
      entity.changeColor(colors.borderColor)
      entity.setBalloonFontColor(colors.fontColor)
      entity.setBalloonFillColor(colors.fillColor)
      if (value === 1) {
        entity.enableIndex()
      } else {
        if (changedShape.previous_balloon_number) {
          entity.setBalloonAdminText(changedShape.previous_balloon_number)
        }
        entity.disableIndex()
      }
      entity.invalidateCache()
    }
    _canvasStore.updateIndex()

    // 전환 후 상태 캡처 → 하나의 undo 엔트리로 등록
    const afterState = captureShapeColorState(shapeId)
    if (!afterState) return
    undoRedo.execute(
      value ? '현재차 전환이' : '전차 전환이',
      async () => restoreShapeColorState(shapeId, beforeState),
      async () => restoreShapeColorState(shapeId, afterState),
    )
  }

  const showDrawing = ref<boolean>(true)
  // 노출처리된 색상들 (여기에 없으면 숨김)
  const filterColors = ref<string[]>([])
  // 각 floor별 필터 상태를 저장하는 맵 (floorId -> colors[])
  const floorFilterStates = ref<Record<string, string[]>>({})
  // 각 floor별 showDrawing 상태를 저장하는 맵 (floorId -> boolean)
  const floorShowDrawingStates = ref<Record<string, boolean>>({})

  /**
   * 특정 floor의 고유한 색상 목록 가져오기
   * @param floorId - 층 ID
   * @returns 색상 배열
   */
  function getFloorUniqueColors(floorId: string): string[] {
    const colors = new Set<string>()

    shapes.value
      .filter((shape: Defect) => !shape.deleted_at)
      .filter((shape: Defect) => shape.floorplan_id.toString() == floorId)
      .filter((shape: Defect) => shape.shape_type !== SHAPES.BRUSH)
      .forEach((shape: Defect) => {
        if (shape.color) {
          colors.add(shape.color)
        }
      })

    return Array.from(colors)
  }

  /**
   * 특정 floor의 필터 상태를 불러오는 함수
   * @param floorId - 층 ID
   */
  function loadFloorFilterState(floorId: string): void {
    const uniqueColors = getFloorUniqueColors(floorId)

    if (floorFilterStates.value[floorId]) {
      filterColors.value = [...floorFilterStates.value[floorId]]
    } else {
      // 저장된 상태가 없으면 모든 색상을 기본으로 선택
      filterColors.value = uniqueColors
      floorFilterStates.value[floorId] = [...filterColors.value]
    }

    if (floorShowDrawingStates.value[floorId] !== undefined) {
      showDrawing.value = floorShowDrawingStates.value[floorId]
    } else {
      showDrawing.value = true
      floorShowDrawingStates.value[floorId] = true
    }
  }

  /**
   * 현재 floor의 필터 상태를 저장하는 함수
   * @param floorId - 층 ID
   */
  function saveFloorFilterState(floorId: string): void {
    floorFilterStates.value[floorId] = [...filterColors.value]
    floorShowDrawingStates.value[floorId] = showDrawing.value
  }

  /**
   * 색상 필터 토글
   * @param color - 색상
   */
  function setFilterColors(color: string): void {
    const isColorInFilter = filterColors.value.includes(color)
    filterColors.value = isColorInFilter
      ? filterColors.value.filter(c => c !== color)
      : [...filterColors.value, color]
  }
  function setFacilityBranchImages(_facilityBranchImages: FacilityPhoto[]): void {
    facilityBranchImages.value = _facilityBranchImages
  }

  function getShapes(floorBranchDatabaseId: number | string): Defect[] {
    return _shapesByFloor.value.get(floorBranchDatabaseId.toString()) ?? []
  }

  // shape 배열을 부모자식관계로 정렬하여 새로운 배열 반환
  function sortShapesWithChildren<T extends Defect>(shapes: T[]): T[] {
    // parent_defect_id → children 맵
    const childrenMap = new Map<string, T[]>()
    const parents: T[] = []

    for (const shape of shapes) {
      if (!shape.parent_defect_id) {
        parents.push(shape)
      } else {
        const key = `${shape.parent_defect_id}`
        let children = childrenMap.get(key)
        if (!children) {
          children = []
          childrenMap.set(key, children)
        }
        children.push(shape)
      }
    }

    // 자식 그룹들 정렬
    for (const [, children] of childrenMap) {
      sortByKeys(children)
    }

    const sortedParents = sortByKeys(parents)
    const result: T[] = []

    function collectChildren(parentId: number | string): void {
      const children = childrenMap.get(`${parentId}`)
      if (!children) return
      for (const child of children) {
        result.push(child)
        collectChildren(child.id)
      }
    }

    for (const parent of sortedParents) {
      result.push(parent)
      collectChildren(parent.id)
    }
    return result
  }

  // floor별 정렬 결과 메모이즈 — shapes 변경당 층별 1회만 정렬 (호출자마다 재정렬하지 않음)
  const _sortedShapesByFloor = computed((): Map<string, Defect[]> => {
    const map = new Map<string, Defect[]>()
    for (const [floorId, floorShapes] of _shapesByFloor.value) {
      map.set(floorId, sortShapesWithChildren(floorShapes))
    }
    return map
  })

  function getSortedShapes(floorBranchDatabaseId: number | string): Defect[] {
    // 호출부의 in-place 변조로부터 맵 내부 배열 보호를 위해 복사본 반환
    return [...(_sortedShapesByFloor.value.get(floorBranchDatabaseId.toString()) ?? [])]
  }

  // 출력용으로 해당 층의 모든 유효한 shapes를 정렬하여 반환
  function getShapesForExport(floorDatabaseId: string): Defect[] {
    const currentShapes = shapes.value
      .filter((shape: Defect) => !shape.deleted_at)
      .filter((shape: Defect) => shape.floorplan_id.toString() == floorDatabaseId)
      .filter(
        (shape: Defect) =>
          !isPreviousShape(shape.previous_defect_id?.toString() ?? '') ||
          (isPreviousShape(shape.previous_defect_id?.toString() ?? '') &&
            getIsPreviousHasDefectPhoto(shape.id.toString())),
      )

    return sortShapesWithChildren(currentShapes)
  }

  function getQuickInputOtherItems(isRange: boolean): QuickInputOtherItem {
    const allShapes = cloneDeep(shapes.value)
    // 모든 shape의 width과 length 속성을 비교하여 제일 많은 값 1,2 위를 반환
    const widthValues: string[] = []
    const lengthValues: string[] = []

    allShapes.forEach((item: Defect) => {
      // 값이 없는 shape는 제외하고 순회 ("0"도 유효하지 않은 값으로 제외)
      if (item.width !== undefined && item.width !== '' && item.width !== '0') {
        if (!WIDTH_VALUES_RANGE.includes(item.width)) {
          const isNumeric = !isNaN(Number(item.width))
          // isRange일 때는 숫자가 아닌 것만, isRange가 false일 때는 숫자인 것만
          if (isRange && !isNumeric) {
            widthValues.push(String(item.width))
          }
          if (!isRange && isNumeric) {
            widthValues.push(String(item.width))
          }
        }
      }
      if (item.length !== undefined && item.length !== '' && item.length !== '0') {
        if (!LENGTH_VALUES.includes(item.length)) {
          lengthValues.push(String(item.length))
        }
      }
    })

    const getTop2 = (values: string[]): string[] => {
      const counts: Record<string, number> = {}
      values.forEach(value => {
        counts[value] = (counts[value] || 0) + 1
      })

      const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1])
      return sorted.slice(0, 2).map(item => item[0])
    }

    const top2Widths = getTop2(widthValues)
    const top2Lengths = getTop2(lengthValues)

    return {
      width: top2Widths,
      length: top2Lengths,
    }
  }

  // skipIndexing: getFacilityNumberingShapes에서 호출 시 true
  // → 전체 층 인덱싱(섹션1)을 건너뛰고 호출측에서 globalIndexing으로 덮어씀
  // → 자식 shape은 호출측에서 filter로 제외되므로 부분 인덱싱 상태가 외부에 노출되지 않음
  function getFacilityFloorShapes(
    floorDatabaseId: string,
    selectedFloorName: string,
    skipIndexing: boolean = false,
  ): Defect[] {
    const sortedShapes = getSortedShapes(floorDatabaseId)
    const photoStore = useWebEditorDataStore()
    const canvasStore = useCanvasStore()
    let indexing = 0

    // 1 전체 층 인덱싱 계산 (skipIndexing=false이고 isAllFloorNumbering 활성화 시)
    if (!skipIndexing && canvasStore.isAllFloorNumbering) {
      const facilityStore = useWebEditorFacilityStore()
      const floorplanStore = useFloorplanStore()
      const currentFloors = floorplanStore.getFloorplans(facilityStore.selectedFacilityDatabaseId)

      if (currentFloors) {
        const currentFloorIndex = currentFloors.findIndex(
          floor => floor.id.toString() === floorDatabaseId,
        )

        // 이전 층들의 인덱싱 합계 계산 최적화
        for (let i = 0; i < currentFloorIndex; i++) {
          const floorShapes = getSortedShapes(currentFloors[i].id)

          for (const shape of floorShapes) {
            if (shape.shape_type === SHAPES.BRUSH || shape.parent_defect_id) continue

            if (isPreviousShape(shape.previous_defect_id?.toString() ?? '')) {
              // 캔버스 풍선 번호와 동일한 규칙: 진행중(progressing==1)이거나 현차 사진이 있으면 순번 부여
              // (도형 없는 전차는 순번을 소비하지 않음)
              const hasCurrentPhoto = photoStore.getIsPreviousHasDefectPhoto(shape.id.toString())
              if (
                !isUnplacedPrevDefect(shape, hasCurrentPhoto) &&
                (shape.progressing == 1 || hasCurrentPhoto)
              ) {
                indexing++
              }
            } else {
              indexing++
            }
          }
        }
      }
    }

    // 2. 부모 요소를 O(1)로 찾기 위한 ID 맵 생성
    const shapeMap = new Map<string | number, Defect>()
    for (const shape of sortedShapes) {
      shapeMap.set(shape.id, shape)
    }

    // 3. 결과 배열 사전 할당 (메모리 재할당 방지)
    const result: Defect[] = []

    // 4. 데이터 가공 및 인덱싱 부여 (단일 순회, toRaw로 Proxy 오버헤드 회피)
    for (const _shape of sortedShapes) {
      const shape = toRaw(_shape)

      // BRUSH 타입은 스킵 (최종 결과에서 제외)
      if (shape.shape_type === SHAPES.BRUSH) {
        shape.localStringId = shape.id.toString()
        shape.indexing = selectedFloorName
        continue
      }

      const shapeIdStr = shape.id.toString()
      shape.localStringId = shapeIdStr

      // 사진 데이터 할당 (allTypePhotos 한 번만 호출)
      const allTypePhotos = photoStore.allTypePhotos(shapeIdStr)
      const newTarget = allTypePhotos?.newTarget
      const newTargetPhotosLength = allTypePhotos?.newTargetPhotos?.length ?? 0

      shape.hasPhoto = isPreviousShape(shape.previous_defect_id?.toString() ?? '')
        ? photoStore.getIsPreviousHasDefectPhoto(shape.id.toString())
        : newTargetPhotosLength > 0
      shape.mainPhotoUrl = newTarget?.url ?? ''
      shape.prevPhotoUrl = allTypePhotos?.prevTarget?.url ?? ''

      // 인덱싱 로직
      if (shape.parent_defect_id) {
        const parent = shapeMap.get(shape.parent_defect_id)
        shape.indexing = parent ? (toRaw(parent).indexing ?? 0) : 0
      } else if (isPreviousShape(shape.previous_defect_id?.toString() ?? '')) {
        const hasCurrentPhoto = photoStore.getIsPreviousHasDefectPhoto(shape.id.toString())
        if (isUnplacedPrevDefect(shape, hasCurrentPhoto)) {
          // 도형 없는 전차(보고서 가져오기): 진행상황과 무관하게 보고서 번호를 보여 주고 순번을 소비하지 않는다
          shape.indexing = shape.previous_balloon_number ?? ''
        } else if (shape.progressing == 1 || hasCurrentPhoto) {
          // 캔버스 풍선 번호와 동일한 규칙: 진행중(progressing==1)이거나 현차 사진이 있으면 순번 부여
          indexing++
          shape.indexing = indexing
        } else {
          shape.indexing = shape.previous_balloon_number ?? 0
        }
      } else {
        indexing++
        shape.indexing = indexing
      }

      result.push(shape)
    }

    return result
  }

  function getFacilityShapes(facilityDatabaseId: string): Defect[] {
    const floorStore = useFloorplanStore()
    const currentFloors = floorStore.getFloorplans(facilityDatabaseId)
    if (!currentFloors) return []
    const facilityShapes: Defect[] = []
    for (const floor of currentFloors) {
      const floorShapes = getFacilityFloorShapes(floor.id.toString(), floor.name)
      facilityShapes.push(...sortShapesWithChildren(floorShapes))
    }
    return facilityShapes
  }

  // 인덱싱 카운터 증가 대상 shape 여부 판별
  function isIndexableShape(shape: Defect, previousCache: Map<string, boolean>): boolean {
    if (shape.shape_type === SHAPES.BRUSH) return false

    const previousId = shape.previous_defect_id?.toString() ?? ''

    // isPreviousShape 결과 캐싱
    if (!previousCache.has(previousId)) {
      previousCache.set(previousId, isPreviousShape(previousId))
    }
    // 전차 항목이 아닌 경우 (일반적인 현회차 항목)
    if (!previousCache.get(previousId)) return true

    const hasCurrentPhoto = getIsPreviousHasDefectPhoto(shape.id.toString())
    // 도형 없는 전차(보고서 가져오기)는 순번을 소비하지 않음
    if (isUnplacedPrevDefect(shape, hasCurrentPhoto)) return false

    // 전차 항목이면서 완료 항목인 경우
    if (shape.progressing === 1) return true

    return hasCurrentPhoto
  }

  function getFacilityNumberingShapes(
    facilityDatabaseId: string,
    selectedFloorName: string,
  ): Defect[] {
    const floorStore = useFloorplanStore()
    const canvasStore = useCanvasStore()

    // 해당 시설의 모든 층을 location 순서대로 가져오기
    const currentFloors = floorStore.getFloorplans(facilityDatabaseId)
    if (!currentFloors) return []

    const shapes: Defect[] = []
    let globalIndexing = 0 // 전체 floor 인덱싱을 위한 전역 카운터
    // 반복 호출되는 함수 결과를 캐싱하여 성능 개선
    const previousCache = new Map<string, boolean>()
    // 루프 내 반복 접근 방지를 위해 변수로 추출
    const isAllFloorNumbering = canvasStore.isAllFloorNumbering

    // 모든 층에 대해 shape 수집 (shape가 없는 층도 포함)
    for (const floor of currentFloors) {
      // 전체 shape를 가져온 후 부모-자식 관계를 고려하여 정렬, 자식 결함 제외
      const floorShapes = sortShapesWithChildren(
        getFacilityFloorShapes(floor.id.toString(), selectedFloorName, true),
      ).filter(shape => !toRaw(shape).parent_defect_id)

      // 전체 floor 인덱싱이 활성화된 경우 연속적인 인덱싱 적용
      if (isAllFloorNumbering) {
        for (const _shape of floorShapes) {
          const shape = toRaw(_shape)
          if (isIndexableShape(shape, previousCache)) {
            shape.indexing = ++globalIndexing
          }
        }
      }

      // spread 대신 apply 사용 (대용량 배열에서 call stack 초과 방지)
      Array.prototype.push.apply(shapes, floorShapes)
    }

    return shapes
  }

  async function addDefectPhotoForPrev(
    taskDatabaseId: string,
    facilityBranchDatabaseId: string,
    floorBranchDatabaseId: string,
    shapeId: shapeId,
    blob: Blob,
    type: DefectPhotoType,
    inspectionType: 'STATUS_OUTSIDE' | 'TEST_MEASURE' = 'STATUS_OUTSIDE',
  ): Promise<{ photoId: string | number; photoUrl: string }> {
    try {
      const file = new File([blob], `${shapeId}_webupload_${Date.now()}.webp`, {
        type: 'image/webp',
      })

      const uploadPath = uploadPathGenerator(
        taskDatabaseId,
        facilityBranchDatabaseId,
        floorBranchDatabaseId,
      )
      const typeUrl =
        type === 'new' ? `${uploadPath}/${file.name}` : `${uploadPath}/prev/${file.name}`

      const resourceUrl = await CloudFile.uploadFile(
        'homecheck-service',
        `${GCS_ROOT_PATH}/${typeUrl}`,
        file,
      )
      if (!resourceUrl) {
        throw new Error('addDefectPhoto failed : 사진 업로드 실패')
      }

      const [defectPhotoResult] = await APIServer.addDefectPhoto([
        {
          defect_id: parseInt(shapeId),
          url: resourceUrl,
          is_previous: type !== 'new',
          inspection_type: inspectionType,
        },
      ])

      const newDefectPhoto: DefectPhoto = {
        ...defectPhotoResult,
        id: defectPhotoResult.id,
      }

      defectPhotos.value.push(newDefectPhoto)

      const changes: ChangedItem[] = findChangedItems(
        [],
        [{ ...newDefectPhoto, localStringId: newDefectPhoto.id.toString() }],
      )
      logChanges('DEFECT_PHOTOS', changes)

      return {
        photoId: defectPhotoResult.id,
        photoUrl: resourceUrl,
      }
    } catch (ex) {
      throw new Error(
        'addDefectPhoto failed : ' + (ex instanceof Error ? ex.message : 'Unknown error'),
      )
    }
  }

  // 이미 업로드된 URL로 전차 사진을 추가
  async function addDefectPhotoFromUrlForPrev(
    shapeId: shapeId,
    resourceUrl: string,
    type: DefectPhotoType,
  ): Promise<{ photoId: string | number; photoUrl: string }> {
    try {
      const [defectPhotoResult] = await APIServer.addDefectPhoto([
        {
          defect_id: parseInt(shapeId),
          url: resourceUrl,
          is_previous: type !== 'new',
        },
      ])

      const newDefectPhoto: DefectPhoto = {
        ...defectPhotoResult,
        id: defectPhotoResult.id,
      }

      defectPhotos.value.push(newDefectPhoto)

      const changes: ChangedItem[] = findChangedItems(
        [],
        [{ ...newDefectPhoto, localStringId: newDefectPhoto.id.toString() }],
      )
      logChanges('DEFECT_PHOTOS', changes)

      return {
        photoId: defectPhotoResult.id,
        photoUrl: resourceUrl,
      }
    } catch (ex) {
      throw new Error(
        'addDefectPhotoFromUrlForPrev failed : ' +
          (ex instanceof Error ? ex.message : 'Unknown error'),
      )
    }
  }

  async function addDefectPhoto(
    taskDatabaseId: string,
    facilityBranchDatabaseId: string,
    floorBranchDatabaseId: string,
    shapeId: shapeId,
    blob: Blob,
    isTestPhoto: boolean = false,
  ): Promise<{ photoId: string | number; photoUrl: string }> {
    try {
      const file = new File([blob], `${shapeId}_webupload_${Date.now()}.webp`, {
        type: 'image/webp',
      })
      const uploadPath = uploadPathGenerator(
        taskDatabaseId,
        facilityBranchDatabaseId,
        floorBranchDatabaseId,
      )
      const resourceUrl = await CloudFile.uploadFile(
        'homecheck-service',
        `${GCS_ROOT_PATH}/${uploadPath}/${file.name}`,
        file,
      )
      if (!resourceUrl) {
        throw new Error('addDefectPhoto failed : 사진 업로드 실패')
      }
      const [defectPhotoResult] = await APIServer.addDefectPhoto([
        {
          defect_id: parseInt(shapeId),
          url: resourceUrl,
          is_previous: false,
          inspection_type: !isTestPhoto ? 'STATUS_OUTSIDE' : 'TEST_MEASURE',
        },
      ])

      const newDefectPhoto: DefectPhoto = {
        ...defectPhotoResult,
        defect_id: parseInt(shapeId),
      }

      defectPhotos.value.push(newDefectPhoto)

      const changes: ChangedItem[] = findChangedItems(
        [],
        [{ ...newDefectPhoto, localStringId: newDefectPhoto.id.toString() }],
      )
      logChanges('DEFECT_PHOTOS', changes)

      return {
        photoId: defectPhotoResult.id,
        photoUrl: resourceUrl,
      }
    } catch (ex) {
      throw new Error(
        'addDefectPhoto failed : ' + (ex instanceof Error ? ex.message : 'Unknown error'),
      )
    }
  }
  async function addDefectPhotoAlreadyUploaded(
    shapeId: shapeId,
    photoItem: DefectPhoto,
  ): Promise<void> {
    try {
      const [defectPhotoResult] = await APIServer.addDefectPhoto([
        {
          ...photoItem,
          defect_id: parseInt(shapeId),
          url: photoItem.url,
          is_previous: photoItem.is_previous,
        },
      ])
      const newDefectPhoto: DefectPhoto = {
        ...defectPhotoResult,
        id: defectPhotoResult.id,
        defect_id: parseInt(shapeId),
      }

      defectPhotos.value.push(newDefectPhoto)

      const changes: ChangedItem[] = findChangedItems(
        [],
        [{ ...newDefectPhoto, localStringId: newDefectPhoto.id.toString() }],
      )
      logChanges('DEFECT_PHOTOS', changes)
    } catch (ex) {
      throw new Error(
        'addDefectPhoto failed : ' + (ex instanceof Error ? ex.message : 'Unknown error'),
      )
    }
  }

  // defect_id 기준 사진 목록
  function getDefectPhotosByShapeId(shapeId: shapeId): DefectPhoto[] {
    return _photosByDefectId.value.get(`${shapeId}`) ?? []
  }

  // 캐시된 사진 목록에서 inspectionType으로 필터
  function getDefectPhotosByShapeIdAndInspectionType(
    shapeId: shapeId,
    inspectionType: InspectionType | 'ALL',
  ): DefectPhoto[] {
    const photos = getDefectPhotosByShapeId(shapeId)
    if (inspectionType === 'ALL') return photos
    return photos.filter(p => p.inspection_type == inspectionType)
  }

  async function removeDefectPhoto(defectPhotoId: string): Promise<void> {
    const findIndex = defectPhotos.value.findIndex(
      defectPhoto => `${defectPhoto.id}` == `${defectPhotoId}`,
    )
    if (findIndex === -1) return

    const findPhoto = defectPhotos.value[findIndex]
    const previousPhoto = { ...findPhoto }

    await APIServer.deleteDefectPhoto([{ id: parseInt(defectPhotoId) }])

    const changedPhoto = { ...findPhoto }
    changedPhoto.deleted_at = Merge.getCurrentTime()

    defectPhotos.value[findIndex] = changedPhoto

    const changes: ChangedItem[] = findChangedItems(
      [{ ...previousPhoto, localStringId: previousPhoto.id.toString() }],
      [{ ...changedPhoto, localStringId: changedPhoto.id.toString() }],
    )
    logChanges('DEFECT_PHOTOS', changes)
  }

  async function restoreDefectPhoto(defectPhotoData: DefectPhoto): Promise<void> {
    const findPhoto = defectPhotos.value.find(
      defectPhoto => `${defectPhoto.id}` == `${defectPhotoData.id}`,
    )
    const isPreviousShapeItem = isPreviousShape(defectPhotoData.defect_id.toString())
    const isNeedMain = getIsPreviousHasDefectCurrentPhoto(defectPhotoData.defect_id.toString())

    const prevPhoto = cloneDeep(findPhoto)
    if (!findPhoto) return
    try {
      await APIServer.restoreDefectPhoto([{ id: Number(defectPhotoData.id) }])

      // restore API는 {successCount} 객체를 반환하므로 기존 데이터로 복원
      const newPhoto = { ...defectPhotoData, deleted_at: null } as DefectPhoto

      if (getIsPreviousHasDefectPhoto(newPhoto.defect_id.toString())) {
        newPhoto.sort_key = getLastSortKey(defectPhotos.value)
      }
      if (isPreviousShapeItem && !isNeedMain) {
        newPhoto.sort_key = getFirstSortKey(defectPhotos.value)
      }

      defectPhotos.value = defectPhotos.value.map(defectPhoto => {
        if (defectPhoto.id == defectPhotoData.id) {
          return newPhoto
        }
        return defectPhoto
      })

      // full-row PUT은 서버가 필드 스탬프로 가드하므로 바뀐 필드는 새 스탬프를 찍어 보낸다
      newPhoto.fields_updated_at = Merge.findChangedField(prevPhoto, newPhoto)
      await APIServer.updateDefectPhoto([
        {
          ...newPhoto,
          id: Number(newPhoto.id),
          sort_key: newPhoto.sort_key,
        },
      ])
    } catch (err) {
      console.error(err)
    }

    const changedPhoto = defectPhotos.value.find(
      defectPhoto => `${defectPhoto.id}` == `${defectPhotoData.id}`,
    )
    if (!prevPhoto || !changedPhoto) return

    const changes: ChangedItem[] = findChangedItems(
      [{ ...prevPhoto, localStringId: prevPhoto?.id.toString() }],
      [{ ...changedPhoto, localStringId: changedPhoto.id.toString() }],
    )
    logChanges('DEFECT_PHOTOS', changes)
  }
  async function onColorChangedDefectPhoto(
    shape: Defect,
    isPreviousHasDefectPhoto: boolean,
    shapeColor: string,
  ): Promise<void> {
    // is_current=1이면 '강제 현차'이므로 사진 유무 무시하고 현차 분기로 처리
    const treatAsCurrent = isPreviousHasDefectPhoto || shape.is_current === 1
    if (treatAsCurrent) {
      // 현차에 해당: 보라색이 아니면 변경 불필요
      if (shapeColor != PREV_DEFECT_STYLE.color) return
      const shapeInStore = shapes.value.find(s => `${s.id}` == `${shape.id}`)
      const saved = shapeInStore ? consumeSavedCurrentStyles(shapeInStore) : undefined
      if (saved) {
        updateColorToHistory(
          shape.id.toString(),
          saved.color,
          saved.balloonFontColor,
          saved.balloonFillColor,
          isPreviousShape(shape.previous_defect_id?.toString() ?? ''),
          true,
        )
      } else {
        const canvasStore = useCanvasStore()
        const styles = canvasStore.getCurrentCreateStyles()
        updateColorToHistory(
          shape.id.toString(),
          styles.balloon_border_color,
          styles.balloon_font_color,
          styles.balloon_fill_color,
          isPreviousShape(shape.previous_defect_id?.toString() ?? ''),
          true,
        )
      }
    } else {
      // 현재차 사진 없음 → 전차(purple)로 전환, 현재 스타일 저장
      const shapeInStore = shapes.value.find(s => `${s.id}` == `${shape.id}`)
      if (shapeInStore && getCurrentColorName(shapeInStore.color) !== 'purple') {
        saveCurrentStylesToShapeData(shapeInStore)
      }
      updateColorToHistory(
        shape.id.toString(),
        PREV_DEFECT_STYLE.color,
        PREV_DEFECT_STYLE.balloonFontColor,
        PREV_DEFECT_STYLE.balloonFillColor,
        isPreviousShape(shape.previous_defect_id?.toString() ?? ''),
        true,
      )
    }
  }

  // inspection_type 변경 등으로 전차/현차 색상 재평가가 필요할 때 호출
  function reevaluatePrevCurrentColor(shapeId: string): void {
    const shape = getShape(shapeId)
    if (!shape) return
    if (!isPreviousShape(shape.previous_defect_id?.toString() ?? '')) return
    const canvasStore = useCanvasStore()
    const currentColor = canvasStore.getShapeColorById(shapeId)?.getColor()
    onColorChangedDefectPhoto(
      shape,
      getIsPreviousHasDefectCurrentPhoto(shapeId),
      currentColor ?? '',
    )
  }

  async function removeDefectPhotoToHistory(defectPhotoId: number | string): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()
    const findIndex = defectPhotos.value.findIndex(
      defectPhoto => `${defectPhoto.id}` == `${defectPhotoId}`,
    )
    if (findIndex === -1) return
    const findPhoto = defectPhotos.value[findIndex]
    await removeDefectPhoto(defectPhotoId.toString())
    // undo에서 복원된 shape ID를 추적하기 위한 변수
    const deletedDefectPhotoId = defectPhotoId
    // 히스토리에만 추가 (이미 실행된 상태)
    const currentIndex = useCanvasStore().getIndex(findPhoto.defect_id.toString())
    undoRedo.execute(
      `${currentIndex ? `${currentIndex}번 ` : ''}결함 사진 삭제가`,
      // 복원 (undo)
      async () => {
        await restoreDefectPhoto(findPhoto)
        const getShapeColor = canvasStore
          .getShapeColorById(findPhoto.defect_id.toString())
          ?.getColor()
        const currentShape = getShape(findPhoto.defect_id.toString())
        if (!currentShape) return
        onColorChangedDefectPhoto(
          currentShape,
          getIsPreviousHasDefectCurrentPhoto(currentShape.id.toString()),
          getShapeColor ?? '',
        )
      },
      // 재삭제 (redo) - executeAction
      async () => {
        await removeDefectPhoto(deletedDefectPhotoId.toString())
        const getShapeColor = canvasStore
          .getShapeColorById(findPhoto.defect_id.toString())
          ?.getColor()
        const currentShape = getShape(findPhoto.defect_id.toString())
        if (!currentShape) return
        onColorChangedDefectPhoto(
          currentShape,
          getIsPreviousHasDefectCurrentPhoto(currentShape.id.toString()),
          getShapeColor ?? '',
        )
      },
    )
  }

  // 사진 추가/삭제에 대한 undo/redo 히스토리 등록
  function registerPhotoUndoRedo(
    shapeId: string,
    addedPhotoId: string,
    savedPhoto: DefectPhoto,
  ): void {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()
    const currentIndex = canvasStore.getIndex(shapeId)
    undoRedo.execute(
      `${currentIndex ? `${currentIndex}번 ` : ''}결함 사진 추가가`,
      async () => {
        await removeDefectPhoto(addedPhotoId)
        const getShapeColor = canvasStore.getShapeColorById(shapeId)?.getColor()
        const currentShape = getShape(shapeId)
        if (!currentShape) return
        onColorChangedDefectPhoto(
          currentShape,
          getIsPreviousHasDefectCurrentPhoto(currentShape.id.toString()),
          getShapeColor ?? '',
        )
      },
      async () => {
        await restoreDefectPhoto(savedPhoto)
        const getShapeColor = canvasStore.getShapeColorById(shapeId)?.getColor()
        const currentShape = getShape(shapeId)
        if (!currentShape) return
        onColorChangedDefectPhoto(
          currentShape,
          getIsPreviousHasDefectCurrentPhoto(currentShape.id.toString()),
          getShapeColor ?? '',
        )
      },
    )
  }

  // 결함 사진 추가 + undo/redo 히스토리 등록
  async function addDefectPhotoToHistory(
    taskDatabaseId: string,
    facilityBranchDatabaseId: string,
    floorBranchDatabaseId: string,
    shapeId: shapeId,
    blob: Blob,
    isTestPhoto: boolean = false,
  ): Promise<{ photoId: string | number; photoUrl: string }> {
    const result = await addDefectPhoto(
      taskDatabaseId,
      facilityBranchDatabaseId,
      floorBranchDatabaseId,
      shapeId,
      blob,
      isTestPhoto,
    )

    const addedPhotoId = result.photoId.toString()
    const addedPhoto = defectPhotos.value.find(p => `${p.id}` == addedPhotoId)
    if (!addedPhoto) return result
    registerPhotoUndoRedo(shapeId, addedPhotoId, cloneDeep(addedPhoto))

    return result
  }

  // 전차 사진 추가 + undo/redo 히스토리 등록
  async function addDefectPhotoForPrevToHistory(
    taskDatabaseId: string,
    facilityBranchDatabaseId: string,
    floorBranchDatabaseId: string,
    shapeId: shapeId,
    blob: Blob,
    type: DefectPhotoType,
    inspectionType: 'STATUS_OUTSIDE' | 'TEST_MEASURE' = 'STATUS_OUTSIDE',
  ): Promise<{ photoId: string | number; photoUrl: string }> {
    const result = await addDefectPhotoForPrev(
      taskDatabaseId,
      facilityBranchDatabaseId,
      floorBranchDatabaseId,
      shapeId,
      blob,
      type,
      inspectionType,
    )

    const addedPhotoId = result.photoId.toString()
    const addedPhoto = defectPhotos.value.find(p => `${p.id}` == addedPhotoId)
    if (!addedPhoto) return result
    registerPhotoUndoRedo(shapeId, addedPhotoId, cloneDeep(addedPhoto))

    return result
  }

  const allTypePhotos = (
    shapeId: shapeId,
  ): {
    prevTarget: DefectPhoto | null
    newTarget: DefectPhoto | null
    prevTargetPhotos: DefectPhoto[]
    newTargetPhotos: DefectPhoto[]
    testTargetPhotos: DefectPhoto[]
  } => {
    const targetPhotos: DefectPhoto[] = getDefectPhotosByShapeId(shapeId)
    const filteringInfoPhotos = targetPhotos.filter(p => isOutSidePhoto(p))
    const filteringTestPhotos = targetPhotos.filter(p => !isOutSidePhoto(p))
    const prevTargetPhotos = sortByKeys(filteringInfoPhotos.filter(p => isPreviousPhoto(p)))
    const newTargetPhotos = sortByKeys(filteringInfoPhotos.filter(p => !isPreviousPhoto(p)))
    return {
      prevTarget: prevTargetPhotos.length > 0 ? prevTargetPhotos[0] : null,
      // prevTarget과 동일하게 정렬 기준 첫 사진 — 앱(defectPhoto.ts)의 newTarget 선택 기준과 일치
      newTarget: newTargetPhotos.length > 0 ? newTargetPhotos[0] : null,
      prevTargetPhotos,
      newTargetPhotos,
      testTargetPhotos: sortByKeys(filteringTestPhotos),
    }
  }

  const getCurrentPrevPhotos = (shapeId: shapeId, type: DefectPhotoType): DefectPhoto[] => {
    const targetPhotos = getDefectPhotosByShapeId(shapeId)
    if (type === null) return targetPhotos
    return sortByKeys(
      targetPhotos.filter(photo =>
        type === 'new' ? !isPreviousPhoto(photo) : isPreviousPhoto(photo),
      ),
    )
  }

  async function setTargetToPrimary(
    shapeId: shapeId,
    defectPhotoId: string,
    type: DefectPhotoType = null,
  ): Promise<void> {
    const filteredDefectPhotos = getCurrentPrevPhotos(shapeId, type)
    if (filteredDefectPhotos.length === 0) return

    const typeTargetPhotos = filteredDefectPhotos.map(photo => ({ ...photo }))
    const findCurrentPhoto = typeTargetPhotos.find(photo => photo.id.toString() == defectPhotoId)
    if (!findCurrentPhoto) return

    const changedPhoto = cloneDeep(findCurrentPhoto)
    changedPhoto.sort_key = getFirstSortKey(typeTargetPhotos)
    // full-row PUT은 서버가 필드 스탬프로 가드하므로 바뀐 필드는 새 스탬프를 찍어 보낸다
    changedPhoto.fields_updated_at = Merge.findChangedField(findCurrentPhoto, changedPhoto)

    // 반응형 데이터 직접 업데이트
    const photoIndex = defectPhotos.value.findIndex(photo => photo.id.toString() == defectPhotoId)
    if (photoIndex !== -1) {
      defectPhotos.value[photoIndex].sort_key = changedPhoto.sort_key
    }

    await APIServer.updateDefectPhoto([changedPhoto])

    const changes: ChangedItem[] = findChangedItems([findCurrentPhoto], [changedPhoto])
    logChanges('DEFECT_PHOTOS', changes)
  }

  async function setPrimaryDefectPhotoForPrev(
    shapeId: shapeId,
    defectPhotoId: string,
    type: DefectPhotoType,
  ): Promise<void> {
    const filteredDefectPhotos = getCurrentPrevPhotos(shapeId, type)
    if (filteredDefectPhotos.length === 0) return

    const typeTargetPhotos = filteredDefectPhotos.map(photo => ({ ...photo }))
    const findCurrentPhoto = typeTargetPhotos.find(photo => photo.id.toString() == defectPhotoId)
    if (!findCurrentPhoto) return

    const changedPhoto = cloneDeep(findCurrentPhoto)
    changedPhoto.sort_key = getFirstSortKey(typeTargetPhotos)
    // full-row PUT은 서버가 필드 스탬프로 가드하므로 바뀐 필드는 새 스탬프를 찍어 보낸다
    changedPhoto.fields_updated_at = Merge.findChangedField(findCurrentPhoto, changedPhoto)

    await APIServer.updateDefectPhoto([changedPhoto])

    const changes: ChangedItem[] = findChangedItems([findCurrentPhoto], [changedPhoto])
    logChanges('DEFECT_PHOTOS', changes)
  }

  /**
   * 결함 사진 그리기 저장 (그림을 모두 지우면 null).
   * deferSync면 메모리만 반영하고 서버 PUT은 syncDefectPhotoDrawing이 한다
   * — 서버 전송 디바운스는 저장 API(photoDrawing/store.ts)가 한다.
   * @param defectPhotoId 사진 id
   * @param drawing 그림 데이터, 그림 없음은 null
   * @param options.deferSync 서버 전송을 미룸
   */
  async function setDefectPhotoDrawing(
    defectPhotoId: string,
    drawing: PhotoDrawing | null,
    options: { deferSync?: boolean } = {},
  ): Promise<void> {
    const photoIndex = defectPhotos.value.findIndex(photo => `${photo.id}` == defectPhotoId)
    if (photoIndex === -1) return
    const findPhoto = defectPhotos.value[photoIndex]
    const nextDrawing = drawing ?? null
    // 기존 객체에는 drawing 키가 없을 수 있다 — 키가 없으면 findChangedField가 스탬프를 찍지 않으므로 null로 맞춘다
    const before = { ...findPhoto, drawing: findPhoto.drawing ?? null }
    // 서버 jsonb는 키 순서를 바꿔 돌려주므로 순서 무관 비교
    if (isSamePhotoDrawing(before.drawing, nextDrawing)) return

    const changedPhoto: DefectPhoto = { ...before, drawing: nextDrawing }
    changedPhoto.fields_updated_at = Merge.findChangedField(before, changedPhoto)
    defectPhotos.value[photoIndex] = changedPhoto

    const changes: ChangedItem[] = findChangedItems(
      [{ ...before, localStringId: before.id.toString() }],
      [{ ...changedPhoto, localStringId: changedPhoto.id.toString() }],
    )
    logChanges('DEFECT_PHOTOS', changes)

    if (!options.deferSync) await syncDefectPhotoDrawing(defectPhotoId)
  }

  /**
   * 메모리의 현재 그림을 서버로 보낸다 (setDefectPhotoDrawing deferSync의 짝)
   * @param defectPhotoId 사진 id
   */
  async function syncDefectPhotoDrawing(defectPhotoId: string): Promise<void> {
    const findPhoto = defectPhotos.value.find(photo => `${photo.id}` == defectPhotoId)
    if (!findPhoto || findPhoto.deleted_at) return
    const stamp = findPhoto.fields_updated_at?.drawing
    await APIServer.updateDefectPhoto([
      {
        id: Number(findPhoto.id),
        drawing: findPhoto.drawing ?? null,
        ...(stamp ? { fields_updated_at: { drawing: stamp } } : {}),
      },
    ])
  }

  /**
   * 주 사진이 있으면 주 사진을 반환, 없으면 다른 사진 중 하나를 반환, 없으면 undefined 반환
   * @param shapeId
   * @returns
   */
  function getDefectPhotoOneByShapeId(shapeId: shapeId): DefectPhoto | undefined {
    const mainPhoto = sortByKeys(
      defectPhotos.value.filter(
        defectPhoto => `${defectPhoto.defect_id}` == `${shapeId}` && !isPreviousPhoto(defectPhoto),
      ),
    )
    if (mainPhoto.length > 0) return mainPhoto[0]

    const otherPhotos = defectPhotos.value.filter(
      defectPhoto => `${defectPhoto.defect_id}` == shapeId,
    )
    if (otherPhotos.length > 0) return otherPhotos[0]

    return undefined
  }

  /**
   * shape의 레이어 ID와 색상을 업데이트합니다
   * @param shapeId - 업데이트할 shape의 ID
   * @param color - 색상
   * @returns Promise<void>
   */
  function updateColor(
    shapeId: string,
    color: string,
    balloonFontColor: string,
    balloonFillColor: string,
  ): void {
    const findShape = shapes.value.find(item => item.id.toString() == shapeId)
    if (!findShape) return
    const previousShape = cloneDeep(findShape)
    const colorName = getCurrentColorName(color)

    const changedShape = { ...findShape }
    //색상 먼저 업데이트
    changedShape.color = color
    changedShape.shape_data.color = color
    changedShape.shape_data.balloonFontColor = balloonFontColor
    changedShape.shape_data.balloonFillColor = balloonFillColor
    changedShape.shape_data.isChanged = isPreviousShape(
      changedShape.previous_defect_id?.toString() ?? '',
    )
      ? false
      : true

    //전차인데 사진이 있는지 확인
    const isPreviousHasDefectPhoto = getIsPreviousHasDefectCurrentPhoto(shapeId)
    if (isPreviousShape(changedShape.previous_defect_id?.toString() ?? '')) {
      if (
        isPreviousHasDefectPhoto &&
        colorName != 'purple' &&
        getCurrentColorName(findShape.color) == 'purple'
      ) {
        const lastSortKey = getLastSortKey(
          shapes.value.filter(
            defect =>
              `${defect.floorplan_id}` == `${findShape.floorplan_id}` &&
              !defect.deleted_at &&
              defect.shape_type !== SHAPES.BRUSH &&
              (!isPreviousShape(defect.previous_defect_id?.toString() ?? '') ||
                (isPreviousShape(defect.previous_defect_id?.toString() ?? '') &&
                  getIsPreviousHasDefectPhoto(defect.id.toString()))),
          ),
        )
        changedShape.sort_key = lastSortKey
      }
    }

    const { afterDefect } = findNeedUpdateDefect(changedShape, previousShape)
    if (!afterDefect) return
    // 스토어 업데이트
    shapes.value = shapes.value.map(shapeItem => {
      if (shapeItem.id.toString() == shapeId) {
        return {
          ...findShape,
          ...changedShape,
        }
      }
      return shapeItem
    })

    const changes: ChangedItem[] = findChangedItems(
      [{ ...previousShape, localStringId: previousShape.id.toString() }],
      [{ ...changedShape, localStringId: changedShape.id.toString() }],
    )
    logChanges('SHAPES', changes)

    // 서버에 요청 (풍선 위치는 서버 기준 rebase 후 전송)
    sendShapesWithRebase([afterDefect], [previousShape]).catch(error => {
      console.error('색상 업데이트 API 오류:', error)
    })
  }
  async function addShapeOnData(shape: Defect): Promise<Defect> {
    const newShape = {
      ...shape,
    }
    const [shapeResult] = await APIServer.addShape([newShape])

    const changes: ChangedItem[] = findChangedItems(
      [],
      [{ ...newShape, id: shapeResult.id, localStringId: newShape.id.toString() }],
    )
    logChanges('SHAPES', changes)
    shapes.value.push({ ...newShape, id: shapeResult.id })
    return shapeResult
  }

  function updateShapes(): void {
    const floorId = useWebEditorFacilityStore().selectedFloorDatabaseId
    const canvasStore = useCanvasStore()
    if (!floorId) return
    const sortedShapes = getSortedShapes(parseInt(floorId))
    const shapeIds = sortedShapes.map(shape => shape.id.toString())
    const isIncludeSelectedShape = shapeIds.includes(selectedShapeId.value)

    // grab 모드가 아닐 때만 선택 해제 처리
    const isGrabMode = canvasStore.isGrabMode || canvasStore.grabbedEntityIds.length > 0
    if (!isIncludeSelectedShape && !isGrabMode) {
      selectShape('')
      canvasStore.cancelSelectedShape()
    }
    canvasStore.sortEntities(shapeIds)
    canvasStore.refreshCache()
  }

  async function copyShapeToHistory(
    shapeId: string,
    copiedShape: CanvasEngineExportEntityType,
  ): Promise<Defect | undefined> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()
    const originalShape = cloneDeep(shapes.value.find(x => x.id.toString() == shapeId))
    if (!originalShape) return

    // 먼저 shape 생성
    let copiedShapeResult = await copyShape(shapeId, copiedShape)
    // undo/redo 등록
    undoRedo.execute(
      '결함 복사가',
      // 삭제 (undo)
      async () => {
        if (!copiedShapeResult) return
        await deleteShape(copiedShapeResult.id.toString())
        canvasStore.canvasDeleteShape(copiedShapeResult.id.toString())
      },
      // 재생성 (redo)
      async () => {
        if (!copiedShapeResult) return
        const newShapeResult = await copyShape(shapeId, copiedShape)
        copiedShapeResult = newShapeResult
        if (!newShapeResult) return
        canvasStore.canvasAddShape(newShapeResult)
        canvasStore.updateIndex()
      },
    )
    return copiedShapeResult
  }

  // shape 다중 삭제
  async function deleteMultipleShapesToHistory(
    shapeIds: string[],
    currentIndices: Map<string, number>,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    // 삭제할 shape들 찾기
    const targetShapes = shapes.value.filter(shape => shapeIds.includes(shape.id.toString()))
    if (targetShapes.length === 0) return

    // 삭제 전 shape 데이터 백업
    const backupShapes = cloneDeep(targetShapes)

    // 모든 shape 데이터 삭제
    await deleteMultipleShapes(shapeIds)

    // undo/redo 등록
    undoRedo.execute(
      `${targetShapes.length}개 결함 삭제가`,
      // 복원 (undo)
      async () => {
        const currentIndexes = backupShapes.map(shape => {
          return currentIndices.get(shape.id.toString()) || 0
        })
        await restoreShapes(backupShapes)
        UTIL.createMultipleEntities(backupShapes, currentIndexes)
        updateShapes()
      },
      // 재삭제 (redo)
      async () => {
        await deleteMultipleShapes(backupShapes.map(shape => shape.id.toString()))
        UTIL.deleteMultipleEntities(backupShapes.map(shape => shape.id.toString()))
      },
    )
  }

  async function onColorChanged(
    shapeId: string,
    color: string,
    fontColor: string,
    fillColor: string,
    isPreviousShape: boolean,
  ): Promise<void> {
    const canvasStore = useCanvasStore()
    const isPurpleColor = color == PREV_DEFECT_STYLE.color
    const currentType = isPreviousShape ? (isPurpleColor ? 'previous' : 'new') : 'new'
    const entity = canvasStore.getEntityById(shapeId.toString())
    if (!entity) return

    canvasStore.changeSelectedEntityColorForPreview(shapeId, color)
    entity.setBalloonFontColor(fontColor)
    entity.setBalloonFillColor(fillColor)
    // 캐시된 이미지가 stale 상태가 되므로 즉시 갱신하여 캔버스에 반영
    entity.invalidateCache()
    if (currentType === 'previous') {
      entity.disableIndex()
    } else {
      entity.enableIndex()
    }
    updateShapes()
    canvasStore.updateIndex()
    UTIL.refreshCache()
  }

  async function updateColorToHistory(
    shapeId: string,
    color: string,
    fontColor: string,
    fillColor: string,
    isPreviousShape: boolean,
    isPhotoChanged?: boolean,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const findShape = shapes.value.find(shape => `${shape.id}` == `${shapeId}`)
    if (!findShape) return
    const originalColor = cloneDeep(findShape.color)
    const originalBalloonFontColor = cloneDeep(findShape.shape_data.balloonFontColor)
    const originalBalloonFillColor = cloneDeep(findShape.shape_data.balloonFillColor)
    updateColor(shapeId, color, fontColor, fillColor)
    onColorChanged(shapeId, color, fontColor, fillColor, isPreviousShape)
    if (isPhotoChanged) return
    undoRedo.execute(
      '색상 변경이',
      async () => {
        updateColor(
          shapeId,
          originalColor,
          originalBalloonFontColor || getCurrentColor('red'),
          originalBalloonFillColor || getCurrentColor('none'),
        )
        onColorChanged(
          shapeId,
          originalColor,
          originalBalloonFontColor || getCurrentColor('red'),
          originalBalloonFillColor || getCurrentColor('none'),
          isPreviousShape,
        )
      },
      async () => {
        updateColor(shapeId, color, fontColor, fillColor)
        onColorChanged(shapeId, color, fontColor, fillColor, isPreviousShape)
      },
    )
  }

  // 여러 shape를 현재 캔버스의 상태로 업데이트
  async function updateMultipleShapes(shapeIds: string[]): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()
    // shapeIds에 해당하는 shapes 찾기
    const targetShapes = shapes.value.filter(
      shape => shapeIds.includes(shape.id.toString()) && !shape.deleted_at,
    )
    if (targetShapes.length === 0) return
    // 이전 shape 데이터 백업
    const originalShapes = cloneDeep(targetShapes)

    // 캔버스에서 현재 상태를 가져와서 각 shape 업데이트
    const updatedShapes = targetShapes.map(shape => {
      const entity = canvasStore.getEntityById(shape.id.toString())
      if (!entity) return shape

      const currentCanvasData = entity.toExportData()
      return {
        ...shape,
        shape_data: currentCanvasData,
      }
    })

    await updateAllShapes(updatedShapes)

    undoRedo.execute(
      '결함 일괄 변경이',
      // 복원 (undo)
      async () => {
        await updateAllShapes(originalShapes)
        // 각 shape를 캔버스에서 리로드
        originalShapes.forEach(shape => {
          canvasStore.reloadShape(shape)
        })
        updateShapes()
        if (canvasStore.isGrabMode === true) canvasStore.getCanvasEngine()?.syncHighlights()
      },
      // 재생성 (redo)
      async () => {
        await updateAllShapes(updatedShapes)
        // 각 shape를 캔버스에서 리로드
        updatedShapes.forEach(shape => {
          canvasStore.reloadShape(shape)
        })
        updateShapes()
        if (canvasStore.isGrabMode === true) canvasStore.getCanvasEngine()?.syncHighlights()
      },
    )
  }

  // 전차 항목을 포함한 여러 shape를 업데이트
  async function updateMultipleShapeToHistoryForPrev(
    updatedShapes: Defect[],
    historyMessage: string,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()

    if (updatedShapes.length === 0) return

    // 이전 데이터 백업
    const shapeIds = updatedShapes.map(s => s.id.toString())
    const originalShapes = cloneDeep(
      shapes.value.filter(shape => shapeIds.includes(shape.id.toString())),
    )

    // 스토어 업데이트
    shapes.value = shapes.value.map(shapeItem => {
      const updatedShape = updatedShapes.find(param => param.id === shapeItem.id)
      return updatedShape ? updatedShape : shapeItem
    })

    // 변경 로깅
    const changes: ChangedItem[] = findChangedItems(
      originalShapes.map(shape => ({ ...shape, localStringId: shape.id.toString() })),
      updatedShapes.map(shape => ({ ...shape, localStringId: shape.id.toString() })),
    )
    logChanges('SHAPES', changes)

    // 캔버스 엔티티 업데이트 (previous_balloon_number 변경 시 balloon text 업데이트)
    updatedShapes.forEach(shape => {
      const entity = canvasStore.getEntityById(shape.id.toString())
      if (entity) {
        // previous_balloon_number 변경 시 balloon text 업데이트
        if (shape.previous_balloon_number !== undefined) {
          entity.setBalloonAdminText(shape.previous_balloon_number!)
        }
        // 렌더링 강제 업데이트
        entity.redraw()
      }
    })

    // 변경된 필드만 추출하여 서버에 요청 (전체 객체 전송 시 빈 필드 검증 에러 방지)
    const partialUpdates = updatedShapes.map(updated => {
      const original = originalShapes.find(o => o.id === updated.id)
      if (!original) return updated
      const changed = Merge.findChangedField(original, updated)
      const partial: Partial<Defect> = { id: updated.id }
      for (const key of Object.keys(changed)) {
        ;(partial as unknown as Record<string, unknown>)[key] = (
          updated as unknown as Record<string, unknown>
        )[key]
      }
      return partial as Defect
    })
    await sendShapesWithRebase(partialUpdates, originalShapes)

    // undo/redo 등록
    undoRedo.execute(
      historyMessage,
      // 복원 (undo)
      async () => {
        // 스토어 복원
        shapes.value = shapes.value.map(shapeItem => {
          const originalShape = originalShapes.find(param => param.id === shapeItem.id)
          return originalShape ? originalShape : shapeItem
        })

        // 캔버스 복원
        originalShapes.forEach(shape => {
          canvasStore.reloadShape(shape)
        })

        // 서버에 복원 요청 (변경된 필드만)
        const undoPartials = originalShapes.map(original => {
          const updated = updatedShapes.find(u => u.id === original.id)
          if (!updated) return original
          const changed = Merge.findChangedField(updated, original)
          const partial: Partial<Defect> = { id: original.id }
          for (const key of Object.keys(changed)) {
            ;(partial as unknown as Record<string, unknown>)[key] = (
              original as unknown as Record<string, unknown>
            )[key]
          }
          return partial as Defect
        })
        await sendShapesWithRebase(undoPartials, updatedShapes)
        updateShapes()
      },
      // 재생성 (redo)
      async () => {
        // 스토어 재적용
        shapes.value = shapes.value.map(shapeItem => {
          const updatedShape = updatedShapes.find(param => param.id === shapeItem.id)
          return updatedShape ? updatedShape : shapeItem
        })

        // 캔버스 재적용
        updatedShapes.forEach(shape => {
          canvasStore.reloadShape(shape)
        })

        // 서버에 재적용 요청 (변경된 필드만, 풍선 위치는 서버 기준 rebase 후)
        await sendShapesWithRebase(partialUpdates, originalShapes)
        updateShapes()
      },
    )
  }

  function getUpdateTargetShapes(floorId: number, isAllFloor: boolean = false): Defect[] {
    const allTaskStore = useAllTaskStore()
    const facilityFloorId = allTaskStore.getFacilityFloorId(floorId)
    if (!facilityFloorId) return []
    const targetShapes = isAllFloor
      ? getFacilityShapes(facilityFloorId.toString())
      : getShapes(floorId.toString())

    if (!targetShapes || targetShapes.length === 0) return []
    return targetShapes.filter(shape => shape.shape_type !== SHAPES.BRUSH)
  }

  async function updateFloorOrFacilityShapes(
    floorBranchDatabaseId: number,
    shapeCanvasData: Partial<CanvasEngineExportEntityType>,
    isAllFloor: boolean = false,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()
    const allTaskStore = useAllTaskStore()

    const facilityFloorId = allTaskStore.getFacilityFloorId(floorBranchDatabaseId)
    if (!facilityFloorId) return

    // floorBranchDatabaseId에 속하는 모든 shape 찾기
    const currentShapes = getUpdateTargetShapes(floorBranchDatabaseId, isAllFloor)
    if (currentShapes.length === 0) return
    const targetShapes = currentShapes.filter(shape => shape.shape_type !== SHAPES.BRUSH)
    if (targetShapes.length === 0) return

    // 이전 shape 데이터 백업
    const originalShapes = cloneDeep(targetShapes)

    // 각 shape의 shape_data를 기존 + 새로운 데이터로 업데이트
    const updatedShapes = targetShapes.map(shape => {
      if (
        !isPreviousShape(shape.previous_defect_id?.toString() ?? '') ||
        (isPreviousShape(shape.previous_defect_id?.toString() ?? '') &&
          getIsPreviousHasDefectPhoto(shape.id.toString()))
      ) {
        return {
          ...shape,
          shape_data: {
            ...shape.shape_data,
            ...shapeCanvasData,
          },
          color: shapeCanvasData.color || shape.color,
        }
      } else {
        //전차는 색상변경 하지않음
        return {
          ...shape,
          shape_data: {
            ...shape.shape_data,
            ...pickShapeScaleFields(shapeCanvasData),
          },
        }
      }
    })

    await updateAllShapes(updatedShapes)

    undoRedo.execute(
      '결함 일괄 변경이',
      // 복원 (undo)
      async () => {
        await updateAllShapes(originalShapes)
        // 각 shape를 캔버스에서 리로드
        originalShapes.forEach(shape => {
          canvasStore.reloadShape(shape)
        })
        updateShapes()
        if (canvasStore.isGrabMode === true) canvasStore.getCanvasEngine()?.syncHighlights()
      },
      // 재생성 (redo)
      async () => {
        await updateAllShapes(updatedShapes)
        // 각 shape를 캔버스에서 리로드
        updatedShapes.forEach(shape => {
          canvasStore.reloadShape(shape)
        })
        updateShapes()
        if (canvasStore.isGrabMode === true) canvasStore.getCanvasEngine()?.syncHighlights()
      },
    )
  }

  //프리셋
  async function getPresetList(): Promise<UserDefectInputPreset[]> {
    return await APIServer.getUserDefectInputPresetList()
  }

  /////////////////////코드정리///////////////////////////
  /**
   * 현재 shape를 찾습니다
   * @param shapeId - 찾을 shape ID
   * @returns Defect | undefined
   */
  function getCurrentDefect(defectId: string): Defect | undefined {
    return shapes.value.find(x => x.id.toString() == defectId.toString())
  }
  /**
   * 업데이트에 필요한 데이터를 찾습니다
   * @param paramDefect - 업데이트할 shape 객체
   * @returns Promise<{ beforeDefect: Partial<Defect> | null, beforeAllDefect: Defect | null, afterDefect: Partial<Defect> | null }>
   * @description beforeDefect: 변경 전 일부 데이터, beforeAllDefect: 변경 전 모든 데이터, afterDefect: 변경 후 일부 데이터
   */
  function findNeedUpdateDefect(
    paramDefect: Defect,
    beforeParamDefect?: Defect,
  ): {
    beforeDefect: Partial<Defect> | null
    beforeAllDefect: Defect | null
    afterDefect: Partial<Defect> | null
  } {
    //1. 기존 데이터 찾기
    const findCurrentShape = shapes.value.find(x => x.id.toString() == paramDefect.id.toString())
    const beforeShape = beforeParamDefect ? beforeParamDefect : cloneDeep(findCurrentShape)
    if (!beforeShape || !findCurrentShape)
      return { beforeDefect: null, beforeAllDefect: null, afterDefect: null }

    //2. 변경된 데이터 찾기
    const changedFields = Merge.findChangedField(beforeShape, paramDefect, true)
    const needUpdateShape: Partial<Defect> = {}
    Object.keys(changedFields).forEach(key => {
      needUpdateShape[key as keyof Defect] = paramDefect[key as keyof Defect]
    })

    //3. before에서도 거르기
    const beforeNeedUpdateShape: Partial<Defect> = {}
    Object.keys(changedFields).forEach(key => {
      beforeNeedUpdateShape[key as keyof Defect] = beforeShape[key as keyof Defect]
    })

    return {
      beforeDefect: { ...beforeNeedUpdateShape, id: paramDefect.id },
      beforeAllDefect: { ...beforeShape, id: paramDefect.id },
      afterDefect: { ...needUpdateShape, id: paramDefect.id },
    }
  }
  /**
   * shape를 업데이트합니다
   * @param shape - 업데이트할 shape 객체
   * @returns Promise<void>
   */

  // shape_data 원본(문자열/객체)에서 balloonPoint 추출
  function readBalloonPoint(raw: unknown): { x: number; y: number } | null {
    try {
      const data = typeof raw === 'string' ? JSON.parse(raw) : raw
      const point = (data as { balloonPoint?: { x: number; y: number } } | null)?.balloonPoint
      return point && typeof point.x === 'number' && typeof point.y === 'number' ? point : null
    } catch {
      return null
    }
  }

  // 결함 id별 전송 직렬화 체인. 같은 결함의 rebase+PUT이 겹치면 앞 요청의 PUT이 뒤 요청의 GET보다 늦게
  // 도착해 뒤 요청이 서버 옛 위치로 rebase하는 등 이 PC의 이동이 되돌아갈 수 있으므로,
  // 앞 요청의 PUT이 끝난 뒤에야 다음 요청의 rebase를 시작한다
  const shapeSendChains = new Map<string, Promise<unknown>>()

  /**
   * 번호풍선 위치를 서버 기준으로 rebase한 뒤 PUT을 보낸다. 같은 결함의 이전 전송이 끝날 때까지 기다린다.
   * @param params 전송 partial 목록 (rebase로 제자리 수정될 수 있음)
   * @param baseShapes 조작 전 스토어 스냅샷 (id로 매칭)
   * @returns 서버 응답 row 목록
   */
  async function sendShapesWithRebase(
    params: Partial<Defect>[],
    baseShapes: Defect[],
  ): Promise<Defect[]> {
    const ids = [...new Set(params.filter(p => p.id !== undefined).map(p => String(p.id)))]
    const previous = Promise.all(ids.map(id => shapeSendChains.get(id) ?? Promise.resolve()))
    const run = previous.then(async () => {
      await rebaseBalloonPointsToServer(params, baseShapes)
      return await APIServer.updateShapes(params)
    })
    const settled = run.catch(() => undefined)
    for (const id of ids) shapeSendChains.set(id, settled)
    try {
      return await run
    } finally {
      for (const id of ids) {
        if (shapeSendChains.get(id) === settled) shapeSendChains.delete(id)
      }
    }
  }

  /**
   * shape_data가 포함된 PUT 직전, 서버 row를 일괄 재조회해 번호풍선 위치를 서버 기준으로 rebase한다.
   * PC 스토어가 낡은 동안(새로고침 없음) 다른 기기가 옮긴 풍선을, 색상·보수완료 등 다른 이유로
   * 전송되는 shape_data 통짜가 옛 위치로 덮는 것을 막는다(서버는 스탬프 없는 partial PUT을 그대로 대입).
   * 이번 조작으로 풍선을 옮긴 경우(전송값 ≠ 조작 전 스토어값)는 사용자의 의도이므로 유지한다.
   * 같은 결함의 이전 전송과 겹치지 않도록 sendShapesWithRebase를 통해서만 호출한다.
   * @param params 전송 예정 partial 목록 (제자리 수정)
   * @param baseShapes 조작 전 스토어 스냅샷 (id로 매칭)
   */
  async function rebaseBalloonPointsToServer(
    params: Partial<Defect>[],
    baseShapes: Defect[],
  ): Promise<void> {
    const baseById = new Map(baseShapes.map(shape => [String(shape.id), shape]))
    const targets = params.filter(
      param => param.id !== undefined && param.shape_data && readBalloonPoint(param.shape_data),
    )
    if (targets.length === 0) return
    let serverById: Map<string, Defect>
    try {
      const serverShapes = await APIServer.getShapes(targets.map(param => String(param.id)))
      serverById = new Map(serverShapes.map(shape => [String(shape.id), shape]))
    } catch (error) {
      // 재조회 실패 시 기존 동작 유지 (전송 강행)
      console.warn('[WebEditor] balloonPoint rebase 조회 실패:', error)
      return
    }
    const canvasStore = useCanvasStore()
    for (const param of targets) {
      const id = String(param.id)
      const serverShape = serverById.get(id)
      const base = baseById.get(id)
      const serverPoint = readBalloonPoint(serverShape?.shape_data)
      const basePoint = readBalloonPoint(base?.shape_data)
      const sentPoint = readBalloonPoint(param.shape_data)
      if (!serverPoint || !basePoint || !sentPoint) continue
      const movedNow = sentPoint.x !== basePoint.x || sentPoint.y !== basePoint.y
      const serverDiffers = serverPoint.x !== basePoint.x || serverPoint.y !== basePoint.y
      if (movedNow || !serverDiffers) continue
      param.shape_data!.balloonPoint = { ...serverPoint }
      console.info(
        `[WebEditor] balloonPoint rebase: id=${id} store=(${basePoint.x},${basePoint.y}) → server=(${serverPoint.x},${serverPoint.y})`,
      )
      const idx = toRaw(shapes.value).findIndex(x => String(x.id) === id)
      if (idx !== -1) {
        const stored = shapes.value[idx].shape_data
        if (stored && typeof stored === 'object') stored.balloonPoint = { ...serverPoint }
      }
      try {
        const entity = canvasStore.getEntityById(id)
        if (entity && !entity.isLine()) entity.moveBallon({ ...serverPoint })
      } catch (error) {
        console.warn('[WebEditor] balloonPoint rebase 캔버스 반영 실패:', error)
      }
    }
  }

  // 달라진 필드만 요소 객체에 직접 대입 — 배열 레벨(요소 교체) invalidate 없이 해당 필드 구독 effect만 재실행
  function patchShapeFields(targetIdx: number, patch: Partial<Defect>): void {
    const raw = toRaw(shapes.value)[targetIdx] as Record<string, unknown>
    const target = shapes.value[targetIdx] as unknown as Record<string, unknown>
    for (const [key, value] of Object.entries(patch)) {
      // shape_data 등 객체 필드 대비 deep 비교, 비교는 raw 기준(track 방지)
      if (!_.isEqual(raw[key], value)) {
        target[key] = value
      }
    }
  }

  async function updateShape(paramDefect: Partial<Defect>): Promise<void> {
    let findShape: Defect | undefined
    try {
      const rawShapes = toRaw(shapes.value)
      const targetIdx = rawShapes.findIndex(x => x.id == paramDefect.id)
      if (targetIdx === -1) return

      findShape = cloneDeep(rawShapes[targetIdx])

      const changedFields = Object.keys(paramDefect).map(key => ({
        key,
        before: findShape![key as keyof Defect],
        after: paramDefect[key as keyof Defect],
      }))
      logChanges('SHAPES', [{ id: findShape.id, isNew: false, changedFields: changedFields }])

      // 로컬 스토어 즉시 반영 (서버 응답 전에도 UI에 반영)
      patchShapeFields(targetIdx, paramDefect)

      // 스토어가 낡은 동안 다른 기기가 옮긴 풍선 위치를 옛 위치로 덮지 않도록 서버 기준 rebase
      const [shapeResult] = await sendShapesWithRebase([paramDefect], [findShape])

      // 서버 응답으로 서버 생성 필드(updated_at 등) 추가 반영
      if (shapeResult) {
        const afterIdx = toRaw(shapes.value).findIndex(x => x.id == paramDefect.id)
        if (afterIdx !== -1) {
          patchShapeFields(afterIdx, shapeResult)
        }
      }
    } catch (error) {
      console.error('Defect 업데이트 오류:', error)
      // 롤백: 서버 실패 시 원래 상태로 복원
      if (findShape) {
        const rollbackIdx = toRaw(shapes.value).findIndex(x => x.id == paramDefect.id)
        if (rollbackIdx !== -1) {
          shapes.value[rollbackIdx] = findShape
        }
      }
    }
  }

  /**
   * shape를 업데이트합니다
   * @param shape - 업데이트할 shape 객체
   * @param isReloadShape - 캔버스 리로드 여부
   * @description UNDO/REDO 기능을 사용하여 shape를 업데이트합니다
   * @returns Promise<void>
   */
  async function updateShapeToHistory(shape: Defect, isReloadShape: boolean = true): Promise<void> {
    const undoRedo = useUndoRedo()
    const shapeParams = cloneDeep(shape)
    const canvasStore = useCanvasStore()
    const { beforeDefect, beforeAllDefect, afterDefect } = findNeedUpdateDefect(shapeParams)
    if (!beforeDefect || !afterDefect || !beforeAllDefect) {
      throw new Error('업데이트에 필요한 데이터가 없습니다')
    }
    await updateShape(afterDefect)

    undoRedo.execute(
      '결함 업데이트가',
      // 복원 (undo)
      async () => {
        await updateShape(beforeDefect)
        if (isReloadShape) {
          if (!beforeAllDefect.shape_data || beforeAllDefect.shape_type === 'NO_SHAPE') {
            // 도형 연결(pending-link) 취소: 결함은 NO_SHAPE 로 돌아가고 캔버스 엔티티만 제거
            canvasStore.removeEntityIfExists(`${beforeAllDefect.id}`)
          } else {
            canvasStore.reloadShape({ ...beforeAllDefect, id: Number(beforeAllDefect.id) })
          }
          updateShapes()
        }
      },
      // 재생성 (redo)
      async () => {
        const needUpdateShape = {
          ...shapeParams,
        }
        await updateShape(afterDefect)
        if (isReloadShape) {
          canvasStore.reloadShape({ ...needUpdateShape })
          updateShapes()
        }
      },
    )
  }

  // 여러 shape의 속성을 업데이트
  async function updateMultipleShapeToHistory(
    defectIds: string[],
    updateFunc: (shape: Defect) => Partial<Defect>,
    historyMessage: string,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()

    // 대상 shape들 찾기
    const targetShapes = shapes.value.filter(
      shape => defectIds.includes(shape.id.toString()) && !shape.deleted_at,
    )
    if (targetShapes.length === 0) return

    // 이전 데이터 백업
    const originalShapes = cloneDeep(targetShapes)

    // 업데이트된 shape들 생성
    const updatedShapes = targetShapes.map(shape => {
      const updates = updateFunc(shape)

      return {
        ...shape,
        ...updates,
        shape_data: updates.shape_data
          ? { ...shape.shape_data, ...updates.shape_data }
          : shape.shape_data,
      }
    })

    // 캔버스 엔티티들에 먼저 변경 사항 적용
    updatedShapes.forEach(shape => {
      const entity = canvasStore.getEntityById(shape.id.toString())
      if (entity && shape.shape_data) {
        // 벌룬 채우기 색상 업데이트
        if (shape.shape_data.balloonFillColor) {
          entity.setBalloonFillColor(shape.shape_data.balloonFillColor)
        }

        // 벌룬 폰트 색상 업데이트
        if (shape.shape_data.balloonFontColor) {
          entity.setBalloonFontColor(shape.shape_data.balloonFontColor)
        }

        // 도형 색상 업데이트
        if (shape.shape_data.color) {
          entity.changeColor(shape.shape_data.color)
        }

        // 렌더링 강제 업데이트
        entity.redraw()
      }
    })

    // 배치 업데이트
    updateAllShapes(updatedShapes).catch(error => {
      console.error('다중 Defect 업데이트 API 오류:', error)
    })

    undoRedo.execute(
      historyMessage,
      // 복원 (undo)
      async () => {
        await updateAllShapes(originalShapes)
        originalShapes.forEach(shape => {
          canvasStore.reloadShape(shape)
        })
        updateShapes()
      },
      // 재생성 (redo)
      async () => {
        await updateAllShapes(updatedShapes)
        updatedShapes.forEach(shape => {
          canvasStore.reloadShape(shape)
        })
        updateShapes()
      },
    )
  }
  async function updateAllShapes(shapesParams: Defect[]): Promise<void> {
    const shapeIds = shapesParams.map(shape => shape.id.toString())
    const findShapes = shapes.value.filter(shape => shapeIds.includes(shape.id.toString()))
    if (findShapes.length === 0) return

    const previousShapes = findShapes.map(shape => cloneDeep(shape))

    const newShapesParams = shapesParams.map(shape => {
      const { afterDefect } = findNeedUpdateDefect(shape)
      return {
        ...afterDefect,
      }
    })
    const result = await sendShapesWithRebase(newShapesParams, previousShapes)
    // 스토어 업데이트
    shapes.value = shapes.value.map(shapeItem => {
      const updatedShape = result.find(param => param.id === shapeItem.id)
      return updatedShape ? updatedShape : shapeItem
    })

    const changes: ChangedItem[] = findChangedItems(
      previousShapes.map(shape => ({ ...shape, localStringId: shape.id.toString() })),
      result.map(shape => ({ ...shape, localStringId: shape.id.toString() })),
    )
    logChanges('SHAPES', changes)

    // 서버에 요청
  }
  /**
   * 새로운 shape를 추가합니다
   * @param _shape - 생성할 shape의 캔버스 데이터
   * @param template - shape 템플릿 타입
   * @returns 새로 생성된 shape의 ID
   */
  async function addShape(newShape: Partial<Defect>): Promise<Defect> {
    const allTaskStore = useAllTaskStore()
    const taskType = allTaskStore.taskType
    if (taskType == 'detailedSafetyInspection') {
      newShape.inspection_type = 'TEST_MEASURE'
    } else {
      newShape.inspection_type = 'STATUS_OUTSIDE'
    }
    // prevData로 전차 등록인지 판단
    const [shapeResult] = await APIServer.addShape([newShape])
    const newShapeItem: Defect = {
      ...shapeResult,
      id: shapeResult.id,
      defect_photos: [] as DefectPhoto[],
    }
    shapes.value.push(newShapeItem)
    const changes: ChangedItem[] = findChangedItems(
      [],
      [{ ...newShapeItem, localStringId: newShapeItem.id.toString() }],
    )
    logChanges('SHAPES', changes)
    return newShapeItem
  }

  // 도면 없이 결함 추가
  async function addShapelessDefect(floorplanId: string): Promise<Defect> {
    const canvasStore = useCanvasStore()
    const currentShapes = shapes.value.filter(s => {
      if (`${s.floorplan_id}` !== floorplanId) return false
      if (s.deleted_at) return false
      if (s.shape_type === 'BRUSH') return false
      // 전차(현차사진없음)는 제외
      if (isPreviousShape(s.previous_defect_id)) {
        return getIsPreviousHasDefectPhoto(`${s.id}`)
      }
      return true
    })
    // 일반 결함과 동일한 색상 결정 (캔버스 설정 우선)
    const canvasColor = canvasStore.getCanvasEngine()?.forCreateBalloonBorderColor
    const defectColor = canvasColor || getCurrentColor('red') || '#FF0000'
    const newShape: Partial<Defect> = {
      shape_type: 'NO_SHAPE',
      shape_data: null,
      floorplan_id: Number(floorplanId),
      template: 'custom-RC' as ShapeTemplateType,
      color: defectColor,
      memo: '',
      progressing: 0,
      width: '',
      length: '',
      area: '',
      count: '',
      is_structural: 0,
      repair_plan: '',
      cause_estimate: '',
      structural_component: '',
      classification: '',
      sort_key: getLastSortKey(currentShapes),
    }
    return await addShape(newShape)
  }

  /**
   * @param _shape : 캔버스 데이터
   * @param template : shape 템플릿 타입
   * @param parent_defect_id : 부모 shape ID
   * @param prevData : 이전 shape 데이터
   * @param floorplan_id : 층평면 ID
   * @returns 새로 생성된 shape의 ID
   */
  async function addShapeToHistory(
    _shape: CanvasEngineExportEntityType,
    template: ShapeTemplateType,
    parent_defect_id: number | null = null,
    prevData: Partial<Defect> | null = null,
    floorplan_id: string,
  ): Promise<Defect> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()
    const filteredPrevData: Partial<Defect> = {}
    if (prevData) {
      Object.keys(prevData).forEach(key => {
        if (
          key !== 'id' &&
          prevData[key as keyof typeof prevData] !== undefined &&
          prevData[key as keyof typeof prevData] !== null &&
          prevData[key as keyof typeof prevData] !== ''
        ) {
          filteredPrevData[key as keyof typeof filteredPrevData] =
            prevData[key as keyof typeof prevData]
        }
      })
    }
    //1. 추가에 필요한 데이터
    const newShape = {
      shape_type: _shape.type,
      floorplan_id: Number(floorplan_id),
      template: template,
      color: _shape.color,
      shape_data: {
        areaBorderThicknessScale: _shape.areaBorderThicknessScale,
        balloonFillColor: _shape.balloonFillColor,
        balloonFontColor: _shape.balloonFontColor,
        balloonFontScale: _shape.balloonFontScale,
        balloonPoint: _shape.balloonPoint,
        balloonScale: _shape.balloonScale,
        color: _shape.color,
        connectedLineThicknessScale: _shape.connectedLineThicknessScale,
        height: _shape.height,
        point: _shape.point,
        pointShapeScale: _shape.pointShapeScale,
        points: _shape.points,
        rotate: _shape.rotate,
        type: _shape.type,
        width: _shape.width,
        brushLines: _shape.brushLines || [],
        dotType: _shape.dotType,
      },
      created_at: Merge.getCurrentTime(),
      ...(prevData
        ? {
            ...filteredPrevData,
          }
        : {}),
      ...(parent_defect_id ? { parent_defect_id: parent_defect_id } : {}),
    }
    // 먼저 shape 생성
    const addedShape = await addShape(newShape)
    // 결함 생성 + 자동 입력을 하나의 undo 엔트리로 병합하기 위해 batch 시작
    const isBrush = _shape.type === 'BRUSH'
    if (!isBrush) undoRedo.startBatch()
    // undo/redo 등록
    undoRedo.execute(
      '결함 생성이',
      // 삭제 (undo)
      async () => {
        await deleteShape(addedShape.id.toString())
        canvasStore.canvasDeleteShape(addedShape.id.toString())
      },
      // 재생성 (redo)
      async () => {
        await restoreShape(addedShape)
        const findShape = shapes.value.find(shape => `${shape.id}` == `${addedShape.id}`)
        if (!findShape) return
        canvasStore.canvasAddShape(findShape)
        updateShapes()
      },
    )
    // batch는 DefectPanel.initData에서 endBatch('결함 생성이')로 종료됨
    return addedShape
  }
  /**
   * shape를 삭제합니다 (소프트 삭제)
   * @param shapeId - 삭제할 shape의 ID
   * @returns Promise<void>
   */
  async function deleteShape(shapeId: string): Promise<void> {
    const findShape = shapes.value.find(shape => shape.id.toString() === shapeId)
    if (!findShape) return

    try {
      await APIServer.deleteShape([{ id: Number(shapeId) }])

      const changedShape = { ...findShape }
      changedShape.deleted_at = Merge.getCurrentTime()

      shapes.value = shapes.value.map(shapeItem => {
        if (shapeItem.id.toString() == shapeId) {
          return changedShape
        }
        return shapeItem
      })

      const changes: ChangedItem[] = findChangedItems(
        [{ ...findShape, localStringId: findShape.id.toString() }],
        [{ ...changedShape, localStringId: changedShape.id.toString() }],
      )
      logChanges('SHAPES', changes)
    } catch (error) {
      console.error('Defect 삭제 오류:', error)
    }
  }
  async function deleteMultipleShapes(shapeIds: string[]): Promise<void> {
    try {
      const findShapes = shapes.value.filter(shape => shapeIds.includes(shape.id.toString()))
      if (findShapes.length === 0) return

      const deletedTime = Merge.getCurrentTime()
      await APIServer.deleteShape(shapeIds.map(shapeId => ({ id: Number(shapeId) })))

      shapes.value = shapes.value.map(shapeItem => {
        if (shapeIds.includes(shapeItem.id.toString())) {
          return { ...shapeItem, deleted_at: deletedTime }
        }
        return shapeItem
      })

      const currentChangedItems: ChangedItem[] = []
      findShapes.forEach(shape => {
        const changes: ChangedItem[] = findChangedItems(
          [{ ...shape, localStringId: shape.id.toString() }],
          [{ ...{ ...shape, deleted_at: deletedTime }, localStringId: shape.id.toString() }],
        )
        currentChangedItems.push(...changes)
      })
      logChanges('SHAPES', currentChangedItems)
    } catch (error) {
      console.error('Defect 삭제 오류:', error)
    }
  }

  /**
   *
   * @param shapeId : 삭제할 shape ID
   * @param currentIndex : 삭제할 shape의 현재 인덱스
   * @returns
   */
  async function deleteShapeToHistory(
    shapeId: number | string,
    currentIndex: number,
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const shape = shapes.value.find(shape => `${shape.id}` == `${shapeId}`)
    if (!shape) return
    await deleteShape(shapeId.toString())
    // undo에서 복원된 shape ID를 추적하기 위한 변수
    const deletedShapeId = shape.id
    // 히스토리에만 추가 (이미 실행된 상태)
    undoRedo.execute(
      '결함 삭제가',
      // 복원 (undo)
      async () => {
        await restoreShape(shape)
        const findShape = shapes.value.find(shape => `${shape.id}` == `${shapeId}`)
        if (!findShape) return
        UTIL.createMultipleEntities([findShape], [currentIndex - 1])
        updateShapes()
      },
      // 재삭제 (redo) - executeAction
      async () => {
        if (deletedShapeId) {
          await deleteShape(deletedShapeId.toString())
          UTIL.deleteMultipleEntities([deletedShapeId.toString()])
        }
      },
    )
  }

  /**
   * 삭제된 shape를 복원합니다
   * @param shapeData : 복원할 shape 데이터
   * @returns
   */
  async function restoreShape(shapeData: Defect): Promise<void> {
    const findShape = shapes.value.find(
      shape => `${shape.id}` == `${shapeData.id}` && shape.deleted_at,
    )
    const prevShape = cloneDeep(findShape)
    if (!findShape) return
    try {
      await APIServer.restoreShape([{ id: Number(shapeData.id) }])

      const newShape = { ...findShape, deleted_at: null }
      shapes.value = shapes.value.map(shape => {
        if (shape.id == shapeData.id) {
          return newShape
        }
        return shape
      })
    } catch (err) {
      console.error(err)
    }

    if (!prevShape) return
    const changedShape = { ...findShape, deleted_at: null }
    const changes: ChangedItem[] = findChangedItems(
      [{ ...prevShape, localStringId: prevShape?.id.toString() }],
      [{ ...changedShape, localStringId: changedShape.id.toString() }],
    )
    logChanges('SHAPES', changes)
  }
  async function restoreShapes(shapeData: Defect[]): Promise<void> {
    const shapeIds = shapeData.map(shape => shape.id)
    const findShapes = shapes.value.filter(shape => shapeIds.includes(shape.id))
    const prevShapes = cloneDeep(findShapes)
    if (!findShapes) return
    try {
      await APIServer.restoreShape(findShapes.map(shape => ({ id: Number(shape.id) })))

      shapes.value = shapes.value.map(shape => {
        if (shapeIds.includes(shape.id)) {
          return { ...shape, deleted_at: null }
        }
        return shape
      })
    } catch (err) {
      console.error(err)
    }

    if (!prevShapes) return
    const changedItems: ChangedItem[] = []
    findShapes.forEach(shape => {
      const changes: ChangedItem[] = findChangedItems(
        [{ ...shape, localStringId: shape.id.toString() }],
        [{ ...{ ...shape, deleted_at: null }, localStringId: shape.id.toString() }],
      )
      changedItems.push(...changes)
    })
    logChanges('SHAPES', changedItems)
  }
  /**
   * TRIPOD 도형을 생성합니다
   * @param tripodExportData - TRIPOD 캔버스 export 데이터
   * @param targetShapeIds - 결합할 point shape들의 ID 배열
   * @returns 새로 생성된 TRIPOD shape
   */
  async function addTripodShape(
    tripodExportData: CanvasEngineExportEntityType,
    targetShapeIds: string[],
  ): Promise<Defect> {
    // 첫 번째 대상 shape 찾기 (가장 빠른 인덱스)
    const targetShapes = sortByKeys(
      targetShapeIds
        .map(id => shapes.value.find(shape => shape.id.toString() == id))
        .filter((shape): shape is Defect => shape != undefined),
    )

    if (targetShapes.length === 0) {
      throw new Error('대상 shape를 찾을 수 없습니다.')
    }

    const baseShape = targetShapes[0]
    const baseShapeData = baseShape.shape_data

    // 첫 번째 point의 balloon 설정들을 TRIPOD 데이터에 병합
    const mergedTripodData: CanvasEngineExportEntityType = {
      ...tripodExportData,
      // 첫 번째 point의 balloon 색상과 스케일 설정들 유지 (undefined인 경우만 기본값 사용)
      balloonFillColor: baseShapeData.balloonFillColor ?? tripodExportData.balloonFillColor,
      balloonFontColor: baseShapeData.balloonFontColor ?? tripodExportData.balloonFontColor,
      balloonScale: baseShapeData.balloonScale ?? tripodExportData.balloonScale,
      connectedLineThicknessScale:
        baseShapeData.connectedLineThicknessScale ?? tripodExportData.connectedLineThicknessScale,
      balloonFontScale: baseShapeData.balloonFontScale ?? tripodExportData.balloonFontScale,
      pointShapeScale: baseShapeData.pointShapeScale ?? tripodExportData.pointShapeScale,
      areaBorderThicknessScale:
        baseShapeData.areaBorderThicknessScale ?? tripodExportData.areaBorderThicknessScale,
    }

    // 기존 shape를 기반으로 새로운 TRIPOD shape 생성
    const newShape: Partial<Defect> = {
      shape_type: SHAPES.TRIPOD,
      shape_data: mergedTripodData, // 병합된 TRIPOD 데이터 사용
      sort_key: baseShape.sort_key,
      color: baseShape.color,
      floorplan_id: baseShape.floorplan_id,
    }

    Object.keys(baseShape).forEach(key => {
      if (baseShape[key as keyof Defect]) {
        if (key == 'shape_data') return
        if (key == 'shape_type') return
        if (key == 'sort_key') return
        if (key == 'color') return
        if (key == 'floorplan_id') return

        newShape[key as keyof Defect] = baseShape[key as keyof Defect]
      }
    })

    // 서버에 새로운 TRIPOD shape 저장
    const [shapeResult] = await APIServer.addShape([newShape as Defect])
    const newShapeItem: Defect = {
      ...shapeResult,
      id: shapeResult.id,
    }

    // 로컬 상태 업데이트
    shapes.value.push(newShapeItem)

    //사진 업로드
    const findPhotos = getDefectPhotosByShapeId(baseShape.id.toString())
    for (const photo of findPhotos) {
      await addDefectPhotoAlreadyUploaded(shapeResult.id.toString(), photo)
    }

    const changes: ChangedItem[] = findChangedItems(
      [],
      [{ ...newShapeItem, localStringId: newShapeItem.id.toString() }],
    )
    logChanges('SHAPES', changes)
    // 기존 point shape들 삭제
    for (const targetId of targetShapeIds) {
      await deleteShape(targetId)
    }

    return newShapeItem
  }

  /**
   * TRIPOD 도형을 히스토리와 함께 생성합니다 (undo/redo 지원)
   * @param tripodExportData - TRIPOD 캔버스 export 데이터
   * @param targetShapeIds - 결합할 point shape들의 ID 배열
   * @returns 새로 생성된 TRIPOD shape
   */
  async function addTripodShapeToHistory(
    tripodExportData: CanvasEngineExportEntityType,
    targetShapeIds: string[],
  ): Promise<Defect> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()

    // 삭제할 shapes 미리 백업
    const targetShapesToDelete = targetShapeIds
      .map(id => shapes.value.find(shape => shape.id.toString() === id))
      .filter((shape): shape is Defect => shape !== undefined)

    // TRIPOD shape 생성
    const addedTripodShape = await addTripodShape(tripodExportData, targetShapeIds)

    // undo/redo 등록
    undoRedo.execute(
      '결합이',
      // 삭제 (undo)
      async () => {
        // TRIPOD 삭제
        await deleteShape(addedTripodShape.id.toString())
        canvasStore.canvasDeleteShape(addedTripodShape.id.toString())

        // 원본 point shapes 복원
        for (const originalShape of targetShapesToDelete) {
          await restoreShape(originalShape)
          const restoredShape = shapes.value.find(
            shape => shape.id.toString() === originalShape.id.toString(),
          )
          if (restoredShape) {
            canvasStore.canvasAddShape(restoredShape)
          }
        }
        updateShapes()
      },
      // 재생성 (redo)
      async () => {
        // 원본 point shapes 삭제
        for (const targetId of targetShapeIds) {
          const existingShape = shapes.value.find(shape => shape.id.toString() === targetId)
          if (existingShape) {
            await deleteShape(targetId)
            canvasStore.canvasDeleteShape(targetId)
          }
        }

        // TRIPOD 복원
        await restoreShape(addedTripodShape)
        const restoredTripod = shapes.value.find(
          shape => shape.id.toString() === addedTripodShape.id.toString(),
        )
        if (restoredTripod) {
          canvasStore.canvasAddShape(restoredTripod)
        }
        updateShapes()
      },
    )

    return addedTripodShape
  }

  /**
   * TRIPOD shape을 개별 POINT shape들로 분해합니다 (undo/redo 지원)
   * @param targetTripod - 분해할 TRIPOD shape
   */
  async function ungroupTripodShapeToHistory(
    targetTripod: Defect,
    newPointEntities: { id: string; shape_data: CanvasEngineExportEntityType }[],
  ): Promise<void> {
    const undoRedo = useUndoRedo()
    const canvasStore = useCanvasStore()
    const originalTripod = cloneDeep(shapes.value.find(x => x.id == targetTripod.id))
    if (!originalTripod) return

    try {
      const createdPointShapes: Defect[] = []
      // 각 POINT shape를 데이터베이스에 생성
      for (const newPointEntity of newPointEntities) {
        const newPointShape: Partial<Defect> = {
          id: Number(newPointEntity.id),
          shape_data: newPointEntity.shape_data,
          shape_type: SHAPES.POINT,
          color: targetTripod.color,
          floorplan_id: targetTripod.floorplan_id,
        }
        Object.keys(targetTripod).forEach(key => {
          if (targetTripod[key as keyof Defect]) {
            if (key == 'shape_data') return
            if (key == 'shape_type') return
            if (key == 'sort_key') return
            if (key == 'color') return
            if (key == 'floorplan_id') return
            newPointShape[key as keyof Defect] = targetTripod[key as keyof Defect]
          }
        })
        const addedPointShape = await addShape(newPointShape)
        createdPointShapes.push(addedPointShape)
        canvasStore.canvasAddShape(addedPointShape)

        //사진 업로드
        const findPhotos = getDefectPhotosByShapeId(targetTripod.id.toString())
        for (const photo of findPhotos) {
          await addDefectPhotoAlreadyUploaded(addedPointShape.id.toString(), photo)
        }
      }

      // TRIPOD 삭제
      await deleteShape(targetTripod.id.toString())
      canvasStore.canvasDeleteShape(targetTripod.id.toString())

      // undo/redo 등록
      undoRedo.execute(
        '결합 해제가',
        // 복원 (undo) - TRIPOD 복원하고 생성된 POINT들 삭제
        async () => {
          // 생성된 POINT들 삭제
          for (const pointShape of createdPointShapes) {
            await deleteShape(pointShape.id.toString())
            canvasStore.canvasDeleteShape(pointShape.id.toString())
          }

          // TRIPOD 복원
          await restoreShape(originalTripod)
          const restoredTripod = shapes.value.find(
            shape => shape.id.toString() === originalTripod.id.toString(),
          )
          if (restoredTripod) {
            canvasStore.canvasAddShape(restoredTripod)
          }
          updateShapes()
        },
        // 재실행 (redo) - TRIPOD 삭제하고 POINT들 다시 생성
        async () => {
          // TRIPOD 삭제
          await deleteShape(targetTripod.id.toString())
          canvasStore.canvasDeleteShape(targetTripod.id.toString())

          // POINT들 복원
          for (const pointShape of createdPointShapes) {
            await restoreShape(pointShape)
            const restoredPoint = shapes.value.find(
              shape => shape.id.toString() === pointShape.id.toString(),
            )
            if (restoredPoint) {
              canvasStore.canvasAddShape(restoredPoint)
            }
          }
          updateShapes()
        },
      )

      updateShapes()
    } catch (error) {
      console.error('TRIPOD 분해 중 오류:', error)
      throw error
    }
  }

  function getFacilityShapesCount(facilityDatabaseId: string): number {
    const currentShapes = shapes.value.filter(
      shape => shape.facility_id.toString() == facilityDatabaseId && !shape.deleted_at,
    )
    return currentShapes.length
  }
  function getFacilityPhotosCount(facilityDatabaseId: string): number {
    const currentPhotos = defectPhotos.value.filter(
      photo => photo.facility_id.toString() == facilityDatabaseId && !photo.deleted_at,
    )
    return currentPhotos.length
  }

  return {
    shapes,
    getShape,
    getShapes,
    deleteShape,
    addShape,
    addTripodShapeToHistory,
    ungroupTripodShapeToHistory,
    copyShape,
    isCheckPreviousShape,
    setIsBecomeCurrent,
    updateShape,
    selectedShapeId,
    setShapes,
    setDefectPhotos,
    selectShape,
    getSortedShapes,
    sortShapesWithChildren,
    getShapesForExport,
    setTargetToPrimary,
    addDefectPhotoForPrev,
    addDefectPhotoFromUrlForPrev,
    allTypePhotos,
    getCurrentPrevPhotos,
    PHOTO_TYPE,
    getFacilityShapes,
    getFacilityNumberingShapes,
    getShapeFloor,
    getDefectPhotosByShapeId,
    getDefectPhotosByShapeIdAndInspectionType,
    getDefectPhotoOneByShapeId,
    setDefectPhotoDrawing,
    syncDefectPhotoDrawing,
    updateFloorOrFacilityShapes,
    getUpdateTargetShapes,
    removeDefectPhoto,
    removeDefectPhotoToHistory,
    addDefectPhoto,
    addDefectPhotoToHistory,
    addDefectPhotoForPrevToHistory,
    setFacilityBranchImages,
    getIsPreviousHasDefectPhoto,
    isPrevWithoutCurrentPhoto,
    getDefectPhotosByTaskId,
    getCurrentDefect,
    filterColors,
    setFilterColors,
    showDrawing,
    getFloorUniqueColors,
    getIsPreviousHasDefectCurrentPhoto,
    reevaluatePrevCurrentColor,
    setPrimaryDefectPhotoForPrev,
    loadFloorFilterState,
    saveFloorFilterState,
    floorFilterStates,
    floorShowDrawingStates,
    addShapeToHistory,
    addShapelessDefect,
    addShapeOnData,
    deleteShapeToHistory,
    deleteMultipleShapesToHistory,
    updateShapeToHistory,
    copyShapeToHistory,
    updateColorToHistory,
    getQuickInputOtherItems,
    getFacilityFloorShapes,
    updateAllShapes,
    updateMultipleShapes,
    updateMultipleShapeToHistory,
    updateMultipleShapeToHistoryForPrev,
    getPresetList,
    updateShapes,
    getFacilityShapesCount,
    getFacilityPhotosCount,
  }
})

export const useWebEditorFacilityStore = defineStore('web-editor-facility', () => {
  const selectedFacilityName = ref('')
  const selectedFloorName = ref('')
  const selectedFacilityDatabaseId = ref('')
  const selectedFloorDatabaseId = ref('')

  const floors = ref<Floorplan[]>([])

  function getCurrentFloors(facilityDatabaseId: string): Floorplan[] {
    const currentFloors = floors.value.filter(
      floor => floor.facility_id.toString() === facilityDatabaseId,
    )
    return currentFloors
  }

  function selectFloor(floorDatabaseId: string): void {
    const allTaskStore = useAllTaskStore()
    const floorplanStore = useFloorplanStore()
    if (allTaskStore.isNotLoaded()) {
      throw new Error('EditorTaskStore is not loaded')
    }
    selectedFloorDatabaseId.value = floorDatabaseId
    selectedFloorName.value = floorplanStore.getFloorplanName(floorDatabaseId)
  }

  function selectFacility(facilityDatabaseId: string): void {
    const actionStore = useWebEditorActionStore()
    actionStore.clearSelection()
    if (facilityDatabaseId == '') {
      selectedFacilityDatabaseId.value = ''
      selectedFacilityName.value = ''
      selectedFloorDatabaseId.value = ''
      selectedFloorName.value = ''
      return
    }
    const allTaskStore = useAllTaskStore()
    if (allTaskStore.isNotLoaded()) {
      throw new Error('EditorTaskStore is not loaded')
    }

    selectedFacilityDatabaseId.value = facilityDatabaseId
    selectedFacilityName.value = allTaskStore.getFacilityName(facilityDatabaseId)
  }

  function clearSelection(): void {
    const dataStore = useWebEditorDataStore()
    const canvasStore = useCanvasStore()
    selectFacility('')
    dataStore.selectShape('')
    canvasStore.setShapeId('')
  }

  return {
    selectedFacilityName,
    selectedFloorName,
    selectedFacilityDatabaseId,
    selectedFloorDatabaseId,
    selectFloor,
    selectFacility,
    getCurrentFloors,
    clearSelection,
  }
})
